import { ArrowLeft01Icon as ArrowLeft, Download01Icon as Download, PaintBucketIcon as Palette, Image01Icon as ImageIcon, ZoomIcon as ZoomIn, Camera01Icon as Camera, Upload01Icon as Upload, ComputerIcon as Monitor, CubeIcon as Box, Tick01Icon as Check, ArrowDown01Icon as ChevronDown, ArrowUp01Icon as ChevronUp, CropIcon as Crop, Delete02Icon as Trash2, RotateLeft01Icon as RefreshCcw, Target01Icon as Target, HandGripIcon as Hand, HelpCircleIcon as CircleHelp, InformationCircleIcon as InfoCircle, Menu01Icon as Menu, TextIcon as Type, TextAlignLeftIcon as AlignLeft, TextAlignCenterIcon as AlignCenter, TextAlignRightIcon as AlignRight, TextAllCapsIcon as CaseUpper, TextIcon as CaseLower, TextIcon as CaseSensitive, TextUnderlineIcon as Underline, TextStrikethroughIcon as Strikethrough, ArrowLeftRightIcon as MoveHorizontal, ArrowUpDownIcon as MoveVertical, SparklesIcon as Sparkles, CursorMagicSelection04Icon, SlidersHorizontalIcon as SlidersHorizontal, Cancel01Icon as X, MusicNote03Icon as Music, VolumeHighIcon as Volume2, VolumeMute01Icon as VolumeX, Move01Icon as Move, PlayIcon as Play, PauseIcon as Pause, Add01Icon as Plus, Search01Icon as Search, Loading01Icon as Loader2, BackgroundIcon, ZoomInAreaIcon, BorderNone01Icon } from 'hugeicons-react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import { useEditorStore, PRESET_BACKGROUNDS, PRESET_SHADOWS, EaseType, PerspectiveType, AnimationType } from '@/store/editor-store'
import { useRecordingStore } from '@/store/recording-store'
import { useProjectStore } from '@/store/project-store'
import { DEMO_VIDEO_PATH, DEMO_ZOOM_REGIONS, DEMO_VIDEO_DURATION } from '@/data/demo-data'
import CanvasPreview from '@/components/CanvasPreview'
import Timeline from '@/components/editor/Timeline'
import HelpModal from '@/components/editor/HelpModal'
import CropModal from '@/components/editor/CropModal'
import ProjectSidebar from '@/components/editor/ProjectSidebar'
import ExportModal from '@/components/editor/ExportModal'
import CursorControls from '@/components/editor/CursorControls'
import ElementControls from '@/components/editor/ElementControls'
import { useExportStore } from '@/store/export-store'
import { MUSIC_TRACKS, MUSIC_CATEGORIES, MusicTrack } from '@/data/music-library'
import { createSynthesizedSFX } from '@/lib/sfx-generator'



// Image library is now handled within BackgroundControls component
import { generateThumbnails } from '@/lib/thumbnail-generator'

export default function EditorPage({ isDemo = false }: { isDemo?: boolean }) {
    const { projectId } = useParams()
    const [activeTab, setActiveTab] = useState<'background' | 'layout' | 'zoom' | 'text' | 'elements' | 'camera' | 'cursor' | 'audio'>('background')
    const blob = useRecordingStore(state => state.blob)
    const cameraBlob = useRecordingStore(state => state.cameraBlob)
    const sourceTitle = useRecordingStore(state => state.sourceTitle)
    const isExtension = useRecordingStore(state => state.isExtension)
    const isDesktop = useRecordingStore(state => state.isDesktop)
    const [isHelpOpen, setIsHelpOpen] = useState(false)
    const [isCropOpen, setIsCropOpen] = useState(false)

    const location = useLocation()
    const [isLoadingDebug, setIsLoadingDebug] = useState(false)
    const [sidebarWidth, setSidebarWidth] = useState(440)
    const [timelineHeight, setTimelineHeight] = useState(350)
    const [isResizingSidebar, setIsResizingSidebar] = useState(false)
    const [isResizingTimeline, setIsResizingTimeline] = useState(false)

    // Store Selectors (Optimized)
    const duration = useEditorStore(state => state.duration)
    const aspectRatio = useEditorStore(state => state.aspectRatio)
    const setAspectRatio = useEditorStore(state => state.setAspectRatio)
    const setThumbnails = useEditorStore(state => state.setThumbnails)

    const setIsSidebarOpen = useProjectStore(state => state.setIsSidebarOpen)
    const saveCurrentProject = useProjectStore(state => state.saveCurrentProject)
    const currentProjectId = useProjectStore(state => state.currentProjectId)
    const createProject = useProjectStore(state => state.createProject)
    const loadProject = useProjectStore(state => state.loadProject)
    const selectedTextLayerId = useEditorStore(state => state.selectedTextLayerId)
    const zoomRegions = useEditorStore(state => state.zoomRegions)

    // Open editor window when in Electron
    useEffect(() => {
        const electron = (window as any).electron
        if (electron?.showEditor) {
            // Wait two frames so React has painted the editor layout before the window appears
            let raf2 = 0
            const raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => { electron.showEditor() })
            })
            // Auto-open Cursor tab for desktop recordings
            if (isDesktop) setActiveTab('cursor')
            return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2) }
        } else {
            // Non-Electron (web): no-op
            if (isDesktop) setActiveTab('cursor')
        }
    }, [])

    // Auto-switch to Zoom tab logic only when a new zoom region is added
    const lastZoomRegionsLength = useRef<number>(0)
    useEffect(() => {
        if (zoomRegions.length > lastZoomRegionsLength.current) {
            setActiveTab('zoom')
        }
        lastZoomRegionsLength.current = zoomRegions.length
    }, [zoomRegions])

    // Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (['INPUT', 'SELECT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) return
            const store = useEditorStore.getState()
            if (e.code === 'Space') {
                e.preventDefault()
                store.setIsPlaying(!store.isPlaying)
            }
            if (e.code === 'KeyS') {
                store.splitClip(store.currentTime)
            }
            if (e.code === 'Delete' || (e.code === 'Backspace' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName))) {
                if (store.selectedClipId) {
                    store.deleteClip(store.selectedClipId)
                    store.setSelectedClip(null)
                }
            }
            if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ') {
                e.preventDefault()
                if (e.shiftKey) {
                    // @ts-ignore
                    useEditorStore.temporal.getState().redo()
                } else {
                    // @ts-ignore
                    useEditorStore.temporal.getState().undo()
                }
            }
            if ((e.ctrlKey || e.metaKey) && e.code === 'KeyY') {
                e.preventDefault()
                // @ts-ignore
                useEditorStore.temporal.getState().redo()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [])

    // Auto-save
    const saveTimerRef = useRef<NodeJS.Timeout>()
    useEffect(() => {
        if (!currentProjectId) return
        const unsubscribe = useEditorStore.subscribe(() => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
            saveTimerRef.current = setTimeout(() => {
                saveCurrentProject()
            }, 1000)
        })
        return () => {
            unsubscribe()
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
        }
    }, [currentProjectId, saveCurrentProject])

    // Initial project creation
    useEffect(() => {
        if (!currentProjectId && blob && !isDemo && !projectId) {
            createProject(sourceTitle || undefined)
        }
    }, [blob, currentProjectId, createProject, isDemo, projectId, sourceTitle])

    // Load project
    useEffect(() => {
        if (projectId && projectId !== currentProjectId) {
            loadProject(projectId)
        }
    }, [projectId, currentProjectId, loadProject])

    // Auto-switch to text tab
    useEffect(() => {
        if (selectedTextLayerId) {
            setActiveTab('text')
        }
    }, [selectedTextLayerId])

    // Demo/Debug Mode
    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const isDebug = params.get('debug') === 'true'
        if ((isDemo || isDebug) && !blob && !projectId) {
            setIsLoadingDebug(true)
            const videoPath = isDemo ? DEMO_VIDEO_PATH : '/screen-recording-1767504339678.webm'
            fetch(videoPath)
                .then(res => {
                    if (!res.ok) throw new Error('Video not found')
                    return res.blob()
                })
                .then(async b => {
                    useRecordingStore.getState().setBlob(b)
                    if (isDemo) {
                        fetch('/Demo-Webcam.mp4')
                            .then(res => {
                                if (res.ok) return res.blob()
                                return null
                            })
                            .then(camBlob => {
                                if (camBlob) {
                                    useRecordingStore.getState().setCameraBlob(camBlob)
                                    useEditorStore.getState().updateCameraLayer({ visible: true })
                                }
                            })
                            .catch(e => console.error("Webcam demo video load failed", e))

                        useEditorStore.getState().reset()
                        useRecordingStore.getState().setMouseData([])
                        const dur = DEMO_VIDEO_DURATION
                        useRecordingStore.getState().setDuration(dur)
                        useEditorStore.getState().setDuration(dur)
                        useEditorStore.getState().initClips(dur)
                        useEditorStore.setState({ zoomRegions: [] })
                        DEMO_ZOOM_REGIONS.forEach(region => {
                            useEditorStore.getState().addZoomRegion(region)
                        })
                    } else {
                        const dur = 46
                        useEditorStore.getState().setDuration(dur)
                        useEditorStore.getState().initClips(dur)
                    }
                    setIsLoadingDebug(false)
                })
                .catch(e => {
                    console.error("Demo load failed", e)
                    if (!isDemo) {
                        fetch('https://vjs.zencdn.net/v/oceans.mp4')
                            .then(res => res.blob())
                            .then(async b => {
                                useRecordingStore.getState().setBlob(b)
                                const dur = 46
                                useEditorStore.getState().setDuration(dur)
                                useEditorStore.getState().initClips(dur)
                                setIsLoadingDebug(false)
                            })
                    } else {
                        setIsLoadingDebug(false)
                    }
                })
        }
    }, [isDemo, blob, location.pathname, projectId])

    // Generate Thumbnails
    const lastGeneratedBlob = useRef<Blob | null>(null)
    const lastGeneratedDuration = useRef<number>(0)
    const isGeneratingThumbnails = useRef(false)
    useEffect(() => {
        if (!blob || duration <= 0) return
        if (isGeneratingThumbnails.current) return
        if (lastGeneratedBlob.current === blob && Math.abs(lastGeneratedDuration.current - duration) < 0.1) return
        isGeneratingThumbnails.current = true
        lastGeneratedBlob.current = blob
        lastGeneratedDuration.current = duration
        generateThumbnails(blob, duration, 24)
            .then(thumbs => {
                if (thumbs.length > 0) {
                    setThumbnails(thumbs)
                }
            })
            .finally(() => {
                isGeneratingThumbnails.current = false
            })
    }, [blob, duration, setThumbnails])

    // Resizing Logic
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isResizingSidebar) {
                const newWidth = window.innerWidth - e.clientX
                setSidebarWidth(Math.max(400, Math.min(600, newWidth)))
            }
            if (isResizingTimeline) {
                const newHeight = window.innerHeight - e.clientY
                setTimelineHeight(Math.max(320, Math.min(600, newHeight)))
            }
        }
        const handleMouseUp = () => {
            setIsResizingSidebar(false)
            setIsResizingTimeline(false)
            document.body.style.cursor = 'default'
        }
        if (isResizingSidebar || isResizingTimeline) {
            window.addEventListener('mousemove', handleMouseMove)
            window.addEventListener('mouseup', handleMouseUp)
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isResizingSidebar, isResizingTimeline])

    const isInitialLoading = (isDemo || projectId) && !blob
    if (isLoadingDebug || isInitialLoading) {
        return (
            <div className="h-screen flex flex-col items-center justify-center bg-surface-950 text-white gap-4">
                <div className="w-12 h-12 border-4 border-brand-500/20 border-t-brand-500 rounded-full animate-spin" />
                <p className="text-sm text-txt-secondary animate-pulse">
                    {projectId ? 'Loading your project...' : 'Preparing demo experience...'}
                </p>
            </div>
        )
    }
    if (!blob) {
        return (
            <div className="h-screen flex items-center justify-center bg-surface-950 text-white">
                <Link to="/" className="text-brand-400 hover:text-brand-300">Start a new recording</Link>
            </div>
        )
    }

    return (
        <div className="h-screen flex flex-col bg-surface-950 text-white overflow-hidden">
            <header className="h-14 border-b border-white/5 flex items-center justify-between bg-surface-900/50 backdrop-blur z-30"
                style={(window as any).electron ? { WebkitAppRegion: 'drag', paddingLeft: '1rem', paddingRight: 0 } as any : { paddingLeft: '1rem', paddingRight: '1rem' }}>
                <div className="flex items-center gap-2" style={{ WebkitAppRegion: 'no-drag' } as any}>
                    <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="p-2 hover:bg-white/5 rounded-lg transition-colors text-txt-secondary hover:text-white"
                    >
                        <Menu size={20} />
                    </button>
                    <div className="h-4 w-px bg-white/10 mx-1" />
                    <Link to="/" onClick={() => (window as any).electron?.setWindowSize?.(900, 56, false, true)}
                        className="p-2 hover:bg-white/5 rounded-lg transition-colors text-txt-secondary hover:text-white">
                        <ArrowLeft size={18} />
                    </Link>
                    {(window as any).electron && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold tracking-wider uppercase rounded-md bg-brand-500/15 text-brand-400 border border-brand-500/25 select-none">
                            Beta
                        </span>
                    )}
                </div>

                <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2" style={{ WebkitAppRegion: 'no-drag' } as any}>
                    <div className="relative group">
                        <button className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-surface-800 hover:bg-surface-700 rounded-lg transition-colors border border-white/5">
                            <Monitor size={14} />
                            <span>{aspectRatio === '16/9' ? 'Wide 16:9' : aspectRatio === '1/1' ? 'Square 1:1' : aspectRatio === '9/16' ? 'Vertical 9:16' : 'Custom'}</span>
                            <ChevronDown size={12} className="text-txt-dim" />
                        </button>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 -mt-2 w-48 hidden group-hover:block z-50">
                            <div className="bg-surface-800 border border-white/10 rounded-lg shadow-xl p-1">
                                {[
                                    { label: 'Wide 16:9', val: '16/9' },
                                    { label: 'Square 1:1', val: '1/1' },
                                    { label: 'Vertical 9:16', val: '9/16' },
                                    { label: 'Classic 4:3', val: '4/3' },
                                ].map((opt) => (
                                    <button
                                        key={opt.val}
                                        onClick={() => setAspectRatio(opt.val)}
                                        className={`w-full text-left px-3 py-2 text-xs rounded-md flex items-center justify-between ${aspectRatio === opt.val ? 'bg-brand-500/10 text-brand-300' : 'hover:bg-white/5 text-txt-secondary'}`}
                                    >
                                        {opt.label}
                                        {aspectRatio === opt.val && <Check size={12} />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => setIsCropOpen(true)}
                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-surface-800 hover:bg-surface-700 rounded-lg transition-colors border border-white/5"
                    >
                        <Crop size={14} />
                        Crop
                    </button>
                </div>

                <div className="flex items-center" style={{ WebkitAppRegion: 'no-drag' } as any}>
                <div className="flex items-center gap-2 px-4">
                        <button
                            onClick={() => setIsHelpOpen(true)}
                            className="flex items-center justify-center w-9 h-9 rounded-xl hover:bg-white/8 transition-colors border border-transparent hover:border-white/10 text-txt-secondary hover:text-white"
                            title="Shortcuts & Tips"
                        >
                            <CircleHelp size={18} />
                        </button>
                        <button
                            onClick={() => useExportStore.getState().openModal()}
                            className="flex items-center gap-2.5 h-9 px-5 text-sm font-semibold rounded-xl bg-brand-500 text-white hover:bg-brand-400 active:scale-[0.97] transition-all shadow-lg shadow-brand-500/25"
                        >
                            <Download size={16} />
                            Export
                        </button>
                    </div>
                    {(window as any).electron && (
                        <div className="flex items-center h-14 border-l border-white/[0.06] ml-1">
                            <button
                                onClick={() => (window as any).electron.minimizeWindow()}
                                className="w-12 h-14 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5 transition-colors"
                                title="Minimize"
                            >
                                <svg width="11" height="1" viewBox="0 0 11 1" fill="currentColor"><rect width="11" height="1.5" rx="0.75"/></svg>
                            </button>
                            <button
                                onClick={() => (window as any).electron.maximizeWindow()}
                                className="w-12 h-14 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5 transition-colors"
                                title="Maximize / Restore"
                            >
                                <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="0.6" y="0.6" width="9.8" height="9.8" rx="1"/></svg>
                            </button>
                            <button
                                onClick={() => (window as any).electron.closeWindow()}
                                className="w-12 h-14 flex items-center justify-center text-white/40 hover:text-white hover:bg-red-500 transition-colors"
                                title="Close"
                            >
                                <X size={13} />
                            </button>
                        </div>
                    )}
                </div>
            </header>

            <div className="flex-1 flex overflow-hidden">
                <div className="flex-1 flex flex-col min-w-0 relative">
                    <div className="flex-1 bg-surface-950 relative overflow-hidden flex items-center justify-center p-8">
                        <CanvasPreview videoBlob={blob!} cameraBlob={cameraBlob} />
                    </div>

                    <div
                        className="h-2 -my-1 cursor-ns-resize z-40 relative group"
                        onMouseDown={(e) => {
                            e.preventDefault()
                            setIsResizingTimeline(true)
                            document.body.style.cursor = 'ns-resize'
                        }}
                    >
                        <div className={`absolute inset-x-0 top-1/2 -translate-y-1/2 transition-all duration-200 ${isResizingTimeline ? 'h-[2px] bg-brand-500' : 'h-[1px] bg-white/5 group-hover:bg-brand-500/50 group-hover:h-[2px]'}`} />
                        <div className={`absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-8 h-1 rounded-full transition-all duration-200 ${isResizingTimeline ? 'bg-brand-400 opacity-100' : 'bg-white/10 opacity-0 group-hover:opacity-100'}`} />
                    </div>

                    <div style={{ height: `${timelineHeight}px` }} className="w-full relative overflow-hidden shrink-0">
                        <Timeline />
                    </div>
                </div>

                <div
                    className="w-2 -mx-1 cursor-ew-resize z-40 relative group shrink-0"
                    onMouseDown={(e) => {
                        e.preventDefault()
                        setIsResizingSidebar(true)
                        document.body.style.cursor = 'ew-resize'
                    }}
                >
                    <div className={`absolute inset-y-0 left-1/2 -translate-x-1/2 transition-all duration-200 ${isResizingSidebar ? 'w-[2px] bg-brand-500' : 'w-[1px] bg-white/5 group-hover:bg-brand-500/50 group-hover:w-[2px]'}`} />
                    <div className={`absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 h-8 w-1 rounded-full transition-all duration-200 ${isResizingSidebar ? 'bg-brand-400 opacity-100' : 'bg-white/10 opacity-0 group-hover:opacity-100'}`} />
                </div>

                <aside
                    style={{ width: `${sidebarWidth}px` }}
                    className="bg-surface-900 border-l border-white/5 flex z-10 shadow-xl shrink-0"
                >
                    <div className="w-14 border-r border-white/5 flex flex-col items-center py-4 gap-4 bg-surface-900">
                        <NavIcon active={activeTab === 'background'} onClick={() => setActiveTab('background')} icon={<BackgroundIcon size={20} />} label="Bg" />
                        <NavIcon active={activeTab === 'layout'} onClick={() => setActiveTab('layout')} icon={<BorderNone01Icon size={20} />} label="Layout" />
                        <NavIcon active={activeTab === 'camera'} onClick={() => setActiveTab('camera')} icon={<Camera size={20} />} label="Camera" />
                        {(isExtension || isDesktop) && (
                            <NavIcon active={activeTab === 'cursor'} onClick={() => setActiveTab('cursor')} icon={<CursorMagicSelection04Icon size={20} />} label="Cursor" />
                        )}
                        <NavIcon active={activeTab === 'zoom'} onClick={() => setActiveTab('zoom')} icon={<ZoomInAreaIcon size={20} />} label="Zoom" />
                        <NavIcon active={activeTab === 'text'} onClick={() => setActiveTab('text')} icon={<Type size={20} />} label="Text" />
                        {/* <NavIcon active={activeTab === 'elements'} onClick={() => setActiveTab('elements')} icon={<ShapesIcon size={20} />} label="Elements" /> */}
                        <NavIcon active={activeTab === 'audio'} onClick={() => setActiveTab('audio')} icon={<Music size={20} />} label="Audio" />
                        {(!isExtension && !isDesktop) && (
                            <NavIcon active={activeTab === 'cursor'} onClick={() => setActiveTab('cursor')} icon={<CursorMagicSelection04Icon size={20} />} label="Cursor" />
                        )}
                    </div>

                    <div className="flex-1 flex flex-col min-w-0">
                        <div className="h-14 border-b border-white/5 flex items-center px-6">
                            <h2 className="text-sm font-medium text-white">
                                {activeTab === 'background' && 'Background'}
                                {activeTab === 'layout' && 'Layout & Video settings'}
                                {activeTab === 'camera' && 'Camera Settings'}
                                {activeTab === 'zoom' && 'Zoom & Keyframes'}
                                {activeTab === 'text' && 'Text customization'}
                                {activeTab === 'elements' && 'Elements & Shapes'}
                                {activeTab === 'cursor' && 'Cursor Settings'}
                                {activeTab === 'audio' && 'Audio Settings'}
                            </h2>
                        </div>
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 space-y-6 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                            {activeTab === 'background' && <BackgroundControls />}
                            {activeTab === 'layout' && <LayoutControls />}
                            {activeTab === 'camera' && <CameraControls />}
                            {activeTab === 'zoom' && <ZoomControls />}
                            {activeTab === 'text' && <TextControls />}
                            {activeTab === 'elements' && <ElementControls />}
                            {activeTab === 'cursor' && <CursorControls />}
                            {activeTab === 'audio' && <AudioControls />}
                        </div>
                    </div>
                </aside>
            </div>

            <HelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
            <CropModal isOpen={isCropOpen} onClose={() => setIsCropOpen(false)} />
            <ProjectSidebar />
            <ExportModal />
        </div>
    )
}

function AudioControls() {
    const audioLayers = useEditorStore(state => state.audioLayers)
    const updateAudioLayer = useEditorStore(state => state.updateAudioLayer)
    const removeAudioLayer = useEditorStore(state => state.removeAudioLayer)
    const addAudioLayer = useEditorStore(state => state.addAudioLayer)
    const recordingVolume = useEditorStore(state => state.recordingVolume)
    const recordingMuted = useEditorStore(state => state.recordingMuted)
    const setRecordingVolume = useEditorStore(state => state.setRecordingVolume)
    const setRecordingMuted = useEditorStore(state => state.setRecordingMuted)

    const [audioMainTab, setAudioMainTab] = useState<'music' | 'sfx'>('music')
    const [selectedCategory, setSelectedCategory] = useState<string>('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [playingTrackId, setPlayingTrackId] = useState<string | null>(null)
    const [loadingTrackId, setLoadingTrackId] = useState<string | null>(null)
    const previewAudioRef = useRef<HTMLAudioElement | null>(null)
    const sfxUploadInputRef = useRef<HTMLInputElement>(null)

    // Cleanup preview on unmount
    useEffect(() => {
        return () => {
            if (previewAudioRef.current) {
                previewAudioRef.current.pause()
                previewAudioRef.current = null
            }
        }
    }, [])

    const handleSFXUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        
        try {
            const url = URL.createObjectURL(file)
            const arrayBuffer = await file.arrayBuffer()
            const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
            const decoded = await audioCtx.decodeAudioData(arrayBuffer)
            await audioCtx.close()

            const channelData = decoded.getChannelData(0)
            const bars = 120
            const blockSize = Math.floor(channelData.length / bars)
            const waveform = Array.from({ length: bars }, (_, i) => {
                let sum = 0
                for (let j = 0; j < blockSize; j++) sum += Math.abs(channelData[i * blockSize + j] || 0)
                return sum / blockSize
            })
            const maxAmp = Math.max(...waveform, 0.001)
            const normalizedWaveform = waveform.map(v => v / maxAmp)

            const currentTime = useEditorStore.getState().currentTime

            addAudioLayer({
                name: file.name.replace(/\.[^/.]+$/, ''),
                type: 'sfx',
                offset: currentTime,
                duration: decoded.duration,
                trimStart: 0,
                volume: 1,
                muted: false,
                url,
                waveform: normalizedWaveform,
                originalDuration: decoded.duration,
            })
        } catch (error) {
            console.error("Failed to decode uploaded SFX:", error)
            alert("Failed to read the audio file. Please ensure it is a valid audio format.")
        } finally {
            e.target.value = ''
        }
    }

    const handlePlayPreview = (track: MusicTrack) => {
        if (playingTrackId === track.id) {
            if (previewAudioRef.current) {
                previewAudioRef.current.pause()
            }
            setPlayingTrackId(null)
        } else {
            if (previewAudioRef.current) {
                previewAudioRef.current.pause()
            }
            let trackUrl = track.url
            if (track.url.startsWith('sfx:')) {
                const sfxType = track.url.replace('sfx:', '')
                trackUrl = createSynthesizedSFX(sfxType)
            }
            const audio = new Audio(trackUrl)
            audio.volume = 0.5
            audio.loop = track.category !== 'sfx'
            previewAudioRef.current = audio
            audio.play().catch(err => console.error("Error playing preview:", err))
            setPlayingTrackId(track.id)
        }
    }

    const handleAddTrack = async (track: MusicTrack) => {
        setLoadingTrackId(track.id)
        if (playingTrackId === track.id && previewAudioRef.current) {
            previewAudioRef.current.pause()
            setPlayingTrackId(null)
        }

        try {
            let trackUrl = track.url
            if (track.url.startsWith('sfx:')) {
                const sfxType = track.url.replace('sfx:', '')
                trackUrl = createSynthesizedSFX(sfxType)
            }

            const response = await fetch(trackUrl)
            const arrayBuffer = await response.arrayBuffer()
            const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
            const decoded = await audioCtx.decodeAudioData(arrayBuffer)
            await audioCtx.close()

            const channelData = decoded.getChannelData(0)
            const bars = 120
            const blockSize = Math.floor(channelData.length / bars)
            const waveform = Array.from({ length: bars }, (_, i) => {
                let sum = 0
                for (let j = 0; j < blockSize; j++) sum += Math.abs(channelData[i * blockSize + j] || 0)
                return sum / blockSize
            })
            const maxAmp = Math.max(...waveform, 0.001)
            const normalizedWaveform = waveform.map(v => v / maxAmp)

            // Place SFX at playhead currentTime, music right after existing audio clips
            const currentPlayhead = useEditorStore.getState().currentTime
            const isSfx = track.category === 'sfx'
            const offsetPos = isSfx
                ? currentPlayhead
                : audioLayers.filter(l => l.type !== 'sfx').reduce((max, l) => Math.max(max, l.offset + l.duration), 0)

            const addAudioLayer = useEditorStore.getState().addAudioLayer
            addAudioLayer({
                name: track.title,
                type: isSfx ? 'sfx' : 'music',
                offset: offsetPos,
                duration: decoded.duration,
                trimStart: 0,
                volume: isSfx ? 0.8 : 0.5,
                muted: false,
                url: trackUrl,
                waveform: normalizedWaveform,
                originalDuration: decoded.duration,
            })
        } catch (error) {
            console.error("Failed to add track to timeline:", error)
            alert("Failed to load and add the audio track. Please try again.")
        } finally {
            setLoadingTrackId(null)
        }
    }

    const availableCategories = audioMainTab === 'music'
        ? MUSIC_CATEGORIES.filter(c => c.id !== 'sfx')
        : MUSIC_CATEGORIES.filter(c => c.id === 'sfx')

    const filteredTracks = MUSIC_TRACKS.filter(track => {
        if (audioMainTab === 'music') {
            if (track.category === 'sfx') return false
            const matchesCategory = selectedCategory === 'all' || track.category === selectedCategory
            const matchesSearch = track.title.toLowerCase().includes(searchQuery.toLowerCase())
            return matchesCategory && matchesSearch
        } else {
            if (track.category !== 'sfx') return false
            const matchesSearch = track.title.toLowerCase().includes(searchQuery.toLowerCase())
            return matchesSearch
        }
    })

    return (
        <div className="space-y-5 animate-in fade-in duration-200 flex flex-col h-full">
            {/* 1. RECORDING AUDIO */}
            <div className="space-y-3 shrink-0">
                <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wider">Recording Audio</label>
                <div className="p-3 bg-surface-800 rounded-xl border border-white/5 space-y-3">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center shrink-0">
                            <Monitor size={13} className="text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs text-white/80 font-medium">Main recording audio</div>
                            <div className="text-[10px] text-white/30 mt-0.5">Mic &amp; desktop audio</div>
                        </div>
                        <button
                            onClick={() => setRecordingMuted(!recordingMuted)}
                            className={`p-1.5 rounded-lg transition-colors ${recordingMuted ? 'bg-white/5 text-white/30 hover:text-white/60' : 'bg-blue-500/10 text-blue-400 hover:text-blue-300'}`}
                            title={recordingMuted ? 'Unmute' : 'Mute'}
                        >
                            {recordingMuted ? <VolumeX size={13} /> : <Volume2 size={13} />}
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-white/30 w-8 shrink-0">Vol</span>
                        <input
                            type="range" min={0} max={1} step={0.05}
                            value={recordingVolume}
                            onChange={(e) => setRecordingVolume(Number(e.target.value))}
                            disabled={recordingMuted}
                            className="flex-1 h-1 accent-blue-500 bg-white/10 rounded-full cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                        />
                        <span className="text-[10px] text-white/40 w-8 text-right shrink-0">{Math.round(recordingVolume * 100)}%</span>
                    </div>
                </div>
            </div>

            {/* 2. ACTIVE LAYERS */}
            {audioLayers.length > 0 && (
                <div className="space-y-2 shrink-0">
                    <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wider">
                        Active Audio Layers ({audioLayers.length})
                    </label>
                    <div className="max-h-[220px] overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                        {audioLayers.map(layer => (
                            <div key={layer.id} className="p-2.5 bg-surface-800 rounded-xl border border-white/5 space-y-2">
                                <div className="flex items-center gap-2">
                                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${layer.type === 'sfx' ? 'bg-pink-500/15' : 'bg-violet-500/15'}`}>
                                        <Music size={12} className={layer.type === 'sfx' ? 'text-pink-400' : 'text-violet-400'} />
                                    </div>
                                    <span className="text-xs text-white/80 font-medium flex-1 truncate" title={layer.name}>{layer.name}</span>
                                    <button
                                        onClick={() => updateAudioLayer(layer.id, { muted: !layer.muted })}
                                        className={`p-1 rounded-lg transition-colors ${layer.muted ? 'bg-white/5 text-white/30 hover:text-white/60' : 'bg-violet-500/10 text-violet-400 hover:text-violet-300'}`}
                                        title={layer.muted ? 'Unmute' : 'Mute'}
                                    >
                                        {layer.muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
                                    </button>
                                    <button
                                        onClick={() => { removeAudioLayer(layer.id); if (layer.url && layer.url.startsWith('blob:')) URL.revokeObjectURL(layer.url) }}
                                        className="p-1 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                        title="Remove"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                                {/* Volume Slider */}
                                <div className="flex items-center gap-2 pl-8">
                                    <span className="text-[9px] text-white/30 w-8 shrink-0">Volume</span>
                                    <input
                                        type="range"
                                        min={0}
                                        max={1}
                                        step={0.05}
                                        value={layer.volume ?? 1}
                                        onChange={(e) => updateAudioLayer(layer.id, { volume: Number(e.target.value) })}
                                        disabled={layer.muted}
                                        className="flex-1 h-1 accent-brand-500 bg-white/10 rounded-full cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    />
                                    <span className="text-[9px] text-white/40 w-8 text-right shrink-0">{Math.round((layer.volume ?? 1) * 100)}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 3. AUDIO LIBRARY WITH MUSIC / SFX SUB-TABS */}
            <div className="space-y-3 pt-4 border-t border-white/5 flex-1 flex flex-col min-h-0 w-full min-w-0">
                {/* Pill Sub-Tab Switcher: Music vs Sound Effects */}
                <div className="flex bg-surface-850 p-1 rounded-xl border border-white/5 gap-1 shadow-inner shrink-0 w-full min-w-0">
                    <button
                        onClick={() => { setAudioMainTab('music'); setSelectedCategory('all') }}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                            audioMainTab === 'music'
                                ? 'bg-brand-500 text-white shadow-md shadow-brand-500/20 scale-[1.02]'
                                : 'text-white/40 hover:text-white hover:bg-white/5'
                        }`}
                    >
                        <Music size={13} /> Music
                    </button>
                    <button
                        onClick={() => { setAudioMainTab('sfx'); setSelectedCategory('sfx') }}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                            audioMainTab === 'sfx'
                                ? 'bg-brand-500 text-white shadow-md shadow-brand-500/20 scale-[1.02]'
                                : 'text-white/40 hover:text-white hover:bg-white/5'
                        }`}
                    >
                        <Volume2 size={13} /> Sound Effects (SFX)
                    </button>
                </div>

                <div className="flex items-center justify-between shrink-0">
                    <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wider flex items-center gap-1.5">
                        <Sparkles size={13} className="text-brand-400" />
                        {audioMainTab === 'music' ? 'Royalty-Free Music' : 'Sound Effects Library'}
                    </label>
                    
                    {/* Instant Custom Responsive Tooltip with 'i' icon */}
                    <div className="relative group/tooltip">
                        <button
                            className="text-white/30 hover:text-white/80 transition-colors p-1 flex items-center"
                            type="button"
                            aria-label="Audio licensing info"
                        >
                            <InfoCircle size={15} />
                        </button>
                        <div className="absolute right-0 top-6 hidden group-hover/tooltip:flex items-center gap-1.5 z-50 bg-surface-800 text-white/90 text-[10px] font-medium py-1.5 px-3 rounded-lg border border-white/10 shadow-2xl whitespace-nowrap animate-in fade-in zoom-in-95 duration-100 pointer-events-none">
                            All music &amp; sound effects are 100% royalty-free CC0 licensed.
                        </div>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="relative shrink-0 w-full min-w-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={13} />
                    <input
                        type="text"
                        placeholder={audioMainTab === 'music' ? 'Search music tracks...' : 'Search sound effects...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-surface-900 border border-white/5 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 transition-colors"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"
                        >
                            <X size={12} />
                        </button>
                    )}
                </div>

                {/* Categories Slider / Subtitle Slot (Fixed height slot to prevent sidebar layout shift) */}
                <div className="h-7 shrink-0 flex items-center w-full min-w-0">
                    {audioMainTab === 'music' && availableCategories.length > 0 ? (
                        <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-thin scrollbar-thumb-white/5 scrollbar-track-transparent w-full">
                            {availableCategories.map((cat) => (
                                <button
                                    key={cat.id}
                                    onClick={() => setSelectedCategory(cat.id)}
                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all shrink-0 border ${
                                        selectedCategory === cat.id
                                            ? 'bg-violet-500/10 border-violet-500/30 text-violet-300 shadow-sm shadow-violet-500/5'
                                            : 'bg-surface-850 border-white/5 text-white/40 hover:bg-surface-800 hover:text-white/80'
                                    }`}
                                >
                                    <span>{cat.icon}</span>
                                    <span>{cat.label}</span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center justify-between w-full min-w-0">
                            <span className="text-[10px] text-white/30 font-medium italic">Royalty-Free &amp; Custom SFX</span>
                            <input
                                ref={sfxUploadInputRef}
                                type="file"
                                accept="audio/*"
                                className="hidden"
                                onChange={handleSFXUpload}
                            />
                            <button
                                onClick={() => sfxUploadInputRef.current?.click()}
                                className="flex items-center gap-1 px-2 py-1 rounded bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/20 hover:border-pink-500/40 text-pink-300 text-[9px] font-bold transition-all active:scale-95 cursor-pointer"
                                title="Upload custom audio file as Sound Effect"
                            >
                                <Upload size={10} className="shrink-0" />
                                <span>Upload SFX</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Tracks List */}
                <div className="space-y-2 flex-1 min-h-[300px] overflow-y-auto pr-1 custom-scrollbar w-full min-w-0">
                    {filteredTracks.length === 0 ? (
                        <div className="text-center py-8 text-white/20 text-xs">
                            No tracks found
                        </div>
                    ) : (
                        filteredTracks.map((track) => {
                            const isPlaying = playingTrackId === track.id
                            const isLoading = loadingTrackId === track.id

                            return (
                                <div
                                    key={track.id}
                                    className={`p-2.5 rounded-xl border transition-all flex items-center gap-3 ${
                                        isPlaying
                                            ? (audioMainTab === 'sfx' ? 'bg-pink-500/5 border-pink-500/20 shadow-lg shadow-pink-500/5' : 'bg-violet-500/5 border-violet-500/20 shadow-lg shadow-violet-500/5')
                                            : 'bg-surface-800 border-white/5 hover:border-white/10 hover:bg-surface-700/50'
                                    }`}
                                >
                                    {/* Play/Pause Button */}
                                    <button
                                        onClick={() => handlePlayPreview(track)}
                                        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all shrink-0 ${
                                            isPlaying
                                                ? 'bg-violet-500 text-white shadow-md shadow-violet-500/20 scale-[1.05]'
                                                : 'bg-surface-900 text-white/60 hover:text-white hover:bg-surface-800 border border-white/5'
                                        }`}
                                        title={isPlaying ? "Pause Preview" : "Play Preview"}
                                    >
                                        {isPlaying ? <Pause size={12} fill="currentColor" /> : <Play size={12} className="ml-0.5" fill="currentColor" />}
                                    </button>

                                    {/* Track Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-white/95 font-semibold truncate flex items-center gap-1.5">
                                            {track.title}
                                            {isPlaying && (
                                                <div className="flex items-end gap-[2px] h-2.5 shrink-0 mb-0.5">
                                                    <span className="w-[1.5px] bg-violet-400 rounded-full animate-audio-bar-1" style={{ height: '4px' }}></span>
                                                    <span className="w-[1.5px] bg-violet-400 rounded-full animate-audio-bar-2" style={{ height: '8px' }}></span>
                                                    <span className="w-[1.5px] bg-violet-400 rounded-full animate-audio-bar-3" style={{ height: '6px' }}></span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-white/30 truncate mt-0.5">
                                            {MUSIC_CATEGORIES.find(c => c.id === track.category)?.label} • CC0 Free
                                        </div>
                                    </div>

                                    {/* Add to Timeline Button */}
                                    <button
                                        onClick={() => handleAddTrack(track)}
                                        disabled={isLoading}
                                        className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all border shrink-0 flex items-center gap-1 ${
                                            isLoading
                                                ? 'bg-white/5 border-white/5 text-white/30 cursor-not-allowed'
                                                : 'bg-white/5 border-white/5 text-white/70 hover:bg-violet-500 hover:border-violet-400 hover:text-white'
                                        }`}
                                        title="Add to Timeline"
                                    >
                                        {isLoading ? (
                                            <>
                                                <Loader2 size={10} className="animate-spin" />
                                                Adding...
                                            </>
                                        ) : (
                                            <>
                                                <Plus size={10} />
                                                Add
                                            </>
                                        )}
                                    </button>
                                </div>
                            )
                        })
                    )}
                </div>
            </div>
        </div>
    )
}


function CameraControls() {
    const { 
        cameraLayer, 
        updateCameraLayer,
        backgroundValue,
        padding,
        borderRadius,
        borderWidth,
        borderColor
    } = useEditorStore()
    const cameraBlob = useRecordingStore(state => state.cameraBlob)

    // Dynamic corner coordinates (safe margin inset)
    const halfW = cameraLayer.size / 2
    const isRect = cameraLayer.mask === 'rectangle'
    const aspectMultiplier = isRect ? 1 : (16 / 9)
    const halfH = (cameraLayer.size / 2) * aspectMultiplier
    const margin = 2

    const minX = halfW + margin
    const maxX = 100 - halfW - margin
    const minY = halfH + margin
    const maxY = 100 - halfH - margin

    const CORNERS = [
        { label: 'Top Left',     x: minX, y: minY },
        { label: 'Top Right',    x: maxX, y: minY },
        { label: 'Bottom Left',  x: minX, y: maxY },
        { label: 'Bottom Right', x: maxX, y: maxY },
    ]

    const isCornerActive = (corner: typeof CORNERS[0]) =>
        Math.abs(cameraLayer.x - corner.x) < 1 && Math.abs(cameraLayer.y - corner.y) < 1

    if (!cameraBlob) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-4 animate-in fade-in duration-300">
                <div className="w-14 h-14 rounded-2xl bg-surface-800 border border-white/5 flex items-center justify-center">
                    <Camera size={24} className="text-white/20" />
                </div>
                <div className="text-center space-y-1.5">
                    <p className="text-sm font-medium text-white/40">No Webcam Recorded</p>
                    <p className="text-[11px] text-white/20 leading-relaxed max-w-[200px] mx-auto">
                        Record with webcam enabled to use camera overlay settings.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-200">

            {/* Show / Hide Toggle */}
            <div className="flex items-center justify-between p-3.5 bg-surface-800 rounded-xl border border-white/5">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-brand-500/15 flex items-center justify-center">
                        <Camera size={15} className="text-brand-400" />
                    </div>
                    <div>
                        <div className="text-xs font-medium text-white">Show Camera</div>
                        <div className="text-[10px] text-white/30 mt-0.5">Webcam overlay visible</div>
                    </div>
                </div>
                <button
                    onClick={() => updateCameraLayer({ visible: !cameraLayer.visible })}
                    className={`w-11 h-6 rounded-full relative transition-all duration-200 ${cameraLayer.visible ? 'bg-brand-500 shadow-lg shadow-brand-500/30' : 'bg-surface-700'}`}
                >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-md transition-all duration-200 ${cameraLayer.visible ? 'left-[26px]' : 'left-1'}`} />
                </button>
            </div>

            {cameraLayer.visible && (
                <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-200">

                    {/* Position Presets */}
                    <div className="space-y-3">
                        <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block">Position</label>

                        {/* Corner Grid with Real background style & inner card */}
                        <div 
                            className="w-full relative rounded-xl border border-white/10 overflow-hidden flex items-center justify-center p-3 shadow-inner bg-surface-950" 
                            style={{
                                aspectRatio: '16/9',
                                background: backgroundValue.includes('gradient') ? backgroundValue : undefined,
                                backgroundColor: backgroundValue.includes('gradient') ? undefined : backgroundValue,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                            }}
                        >
                            {/* Inner video card mockup */}
                            <div 
                                className="w-full h-full bg-[#08080f]/80 relative shadow-md transition-all duration-300"
                                style={{
                                    borderRadius: `${Math.max(2, borderRadius / 5)}px`,
                                    border: borderWidth > 0 ? `${Math.max(1, borderWidth / 4)}px solid ${borderColor}` : undefined,
                                    padding: `${padding / 6}%`
                                }}
                            >
                                {/* Overlay representing the Camera position */}
                                <div 
                                    className="absolute bg-brand-500 border border-brand-400 rounded-lg flex items-center justify-center transition-all duration-300 shadow-md shadow-brand-500/30"
                                    style={{
                                        left: `${cameraLayer.x}%`,
                                        top: `${cameraLayer.y}%`,
                                        width: `${cameraLayer.size}%`,
                                        aspectRatio: cameraLayer.mask === 'rectangle' ? '16/9' : '1/1',
                                        transform: 'translate(-50%, -50%)',
                                        borderRadius: cameraLayer.mask === 'circle' ? '50%' : `${cameraLayer.borderRadius ?? 35}%`,
                                    }}
                                >
                                    <Camera size={10} className="text-white shrink-0 scale-75" />
                                </div>
                            </div>

                            {/* Four corner click buttons overlay */}
                            {CORNERS.map((corner) => {
                                const active = isCornerActive(corner)
                                return (
                                    <button
                                        key={corner.label}
                                        onClick={() => updateCameraLayer({ x: corner.x, y: corner.y })}
                                        title={corner.label}
                                        className={`absolute w-8 h-8 rounded-lg border flex items-center justify-center transition-all duration-150 z-10 ${
                                            active
                                                ? 'bg-brand-500 border-brand-400 shadow-lg shadow-brand-500/40 scale-105'
                                                : 'bg-black/60 hover:bg-black/80 border-white/10 hover:border-white/20'
                                        }`}
                                        style={{
                                            left: corner.x > 50 ? 'auto' : '8px',
                                            right: corner.x > 50 ? '8px' : 'auto',
                                            top: corner.y > 50 ? 'auto' : '8px',
                                            bottom: corner.y > 50 ? '8px' : 'auto',
                                        }}
                                    >
                                        <Camera size={12} className={active ? 'text-white' : 'text-white/40'} />
                                    </button>
                                )
                            })}
                        </div>

                        {/* Corner label pills */}
                        <div className="grid grid-cols-2 gap-1.5">
                            {CORNERS.map((corner) => {
                                const active = isCornerActive(corner)
                                return (
                                    <button
                                        key={corner.label}
                                        onClick={() => updateCameraLayer({ x: corner.x, y: corner.y })}
                                        className={`py-1.5 rounded-lg text-[10px] font-semibold transition-all border ${
                                            active
                                                ? 'bg-brand-500/15 border-brand-500/40 text-brand-300'
                                                : 'bg-surface-800 border-white/5 text-white/30 hover:text-white/60 hover:border-white/15'
                                        }`}
                                    >
                                        {corner.label}
                                    </button>
                                )
                            })}
                        </div>

                        {/* Manual drag hint */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-brand-500/5 rounded-lg border border-brand-500/10">
                            <Hand size={11} className="text-brand-400 shrink-0" />
                            <span className="text-[10px] text-brand-300/80">Drag the webcam circle on the preview to place freely</span>
                        </div>
                    </div>

                    {/* Shape */}
                    <div className="space-y-2.5">
                        <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block">Shape</label>
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { id: 'circle',    label: 'Circle',    icon: <div className="w-4 h-4 rounded-full bg-current" /> },
                                { id: 'square',    label: 'Square',    icon: <div className="w-4 h-4 rounded-md bg-current" /> },
                                { id: 'rectangle', label: 'Wide',      icon: <div className="w-5 h-3 rounded-sm bg-current" /> },
                            ].map((shape) => (
                                <button
                                    key={shape.id}
                                    onClick={() => updateCameraLayer({ mask: shape.id as any })}
                                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all ${
                                        cameraLayer.mask === shape.id
                                            ? 'bg-brand-500/10 border-brand-500/60 text-white shadow-lg shadow-brand-500/10'
                                            : 'bg-surface-800 border-white/5 text-white/30 hover:bg-surface-700 hover:text-white/60'
                                    }`}
                                >
                                    {shape.icon}
                                    <span className="text-[10px] font-medium">{shape.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Size */}
                    <div className="space-y-2.5">
                        <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block">Size</label>
                        <div className="flex items-center gap-3 p-3 bg-surface-800 rounded-xl border border-white/5">
                            <input
                                type="range" min={5} max={50} step={1}
                                value={cameraLayer.size}
                                onChange={(e) => updateCameraLayer({ size: Number(e.target.value) })}
                                className="flex-1 h-1.5 accent-brand-500 rounded-full cursor-pointer"
                            />
                            <span className="text-xs font-semibold text-brand-400 w-10 text-right shrink-0">{cameraLayer.size}%</span>
                            <button
                                onClick={() => updateCameraLayer({ size: 20 })}
                                className="text-white/20 hover:text-white/60 transition-colors"
                                title="Reset"
                            >
                                <RefreshCcw size={12} />
                            </button>
                        </div>
                    </div>

                    {/* Corner Radius */}
                    {cameraLayer.mask !== 'circle' && (
                        <div className="space-y-2.5 animate-in fade-in slide-in-from-top-1 duration-150">
                            <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block">Corner Radius</label>
                            <div className="flex items-center gap-3 p-3 bg-surface-800 rounded-xl border border-white/5">
                                <input
                                    type="range" min={0} max={50} step={1}
                                    value={cameraLayer.borderRadius ?? 35}
                                    onChange={(e) => updateCameraLayer({ borderRadius: Number(e.target.value) })}
                                    className="flex-1 h-1.5 accent-brand-500 rounded-full cursor-pointer"
                                />
                                <span className="text-xs font-semibold text-brand-400 w-10 text-right shrink-0">{cameraLayer.borderRadius ?? 35}%</span>
                                <button
                                    onClick={() => updateCameraLayer({ borderRadius: 35 })}
                                    className="text-white/20 hover:text-white/60 transition-colors"
                                    title="Reset to default (35%)"
                                >
                                    <RefreshCcw size={12} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Border */}
                    <div className="space-y-2.5">
                        <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block">Border</label>
                        <div className="p-3 bg-surface-800 rounded-xl border border-white/5 space-y-3">
                            <div className="flex items-center gap-3">
                                <input
                                    type="range" min={0} max={10} step={1}
                                    value={cameraLayer.borderWidth}
                                    onChange={(e) => updateCameraLayer({ borderWidth: Number(e.target.value) })}
                                    className="flex-1 h-1.5 accent-brand-500 rounded-full cursor-pointer"
                                />
                                <span className="text-xs font-semibold text-white/40 w-8 text-right shrink-0">{cameraLayer.borderWidth}px</span>
                            </div>
                            {cameraLayer.borderWidth > 0 && (
                                <div className="flex items-center gap-3 pt-2 border-t border-white/5">
                                    <span className="text-[10px] text-white/30 flex-1">Color</span>
                                    <div className="relative flex items-center gap-2">
                                        <div className="w-5 h-5 rounded-full border border-white/10" style={{ background: cameraLayer.borderColor }} />
                                        <input
                                            type="color"
                                            value={cameraLayer.borderColor}
                                            onChange={(e) => updateCameraLayer({ borderColor: e.target.value })}
                                            className="w-8 h-7 rounded-lg bg-transparent border-0 cursor-pointer p-0 opacity-0 absolute"
                                        />
                                        <span className="text-[10px] font-mono text-white/40">{cameraLayer.borderColor}</span>
                                    </div>
                                    <input
                                        type="color"
                                        value={cameraLayer.borderColor}
                                        onChange={(e) => updateCameraLayer({ borderColor: e.target.value })}
                                        className="w-7 h-7 rounded-lg bg-surface-700 border border-white/10 cursor-pointer p-0.5 shrink-0"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            )}
        </div>
    )
}

function NavIcon({ active, onClick, icon, label }: any) {
    return (
        <button
            onClick={onClick}
            className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center gap-1 transition-all ${active ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'text-txt-dim hover:bg-white/5 hover:text-white'}`}
            title={label}
        >
            {icon}
            {/* <span className="text-[10px]">{label}</span> */}
        </button>
    )
}

function BackgroundControls() {
    const {
        backgroundValue,
        backgroundType,
        backgroundBlur,
        backgroundDim,
        backgroundNoise,
        backgroundAttribution,
        setBackground,
        setBackgroundBlur,
        setBackgroundDim,
        setBackgroundNoise
    } = useEditorStore()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [bgSubTab, setBgSubTab] = useState<'gradients' | 'photos'>(backgroundType === 'image' ? 'photos' : 'gradients')
    const [gradientCategory, setGradientCategory] = useState<'all' | 'animated' | 'holographic' | 'texture' | 'linear'>('all')

    const filteredGradients = gradientCategory === 'all'
        ? PRESET_BACKGROUNDS
        : PRESET_BACKGROUNDS.filter(g => g.category === gradientCategory)
    const [selectedCategory, setSelectedCategory] = useState('X-Supply')
    const [pexelsCache, setPexelsCache] = useState<Record<string, any[]>>({})
    const [isLoadingPexels, setIsLoadingPexels] = useState(false)
    const [isPreFetching, setIsPreFetching] = useState(false)
    const tabsRef = useRef<HTMLDivElement>(null)
    const gradientTabsRef = useRef<HTMLDivElement>(null)

    const IMAGE_CATEGORIES = [
        'X-Supply', 'Abstracts', 'Landscapes', 'Space', 'Greens', 'Ocean', 'Hills', 'Fire', 'Forests', 'Arctic', 'Retro', 'Minimal'
    ]

    // Auto-scroll active tab into view
    useEffect(() => {
        if (!tabsRef.current) return
        const activeTab = tabsRef.current.querySelector(`[data-category="${selectedCategory}"]`)
        if (activeTab) {
            activeTab.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            })
        }
    }, [selectedCategory])

    // Auto-scroll active gradient category chip into view
    useEffect(() => {
        if (!gradientTabsRef.current) return
        const activeTab = gradientTabsRef.current.querySelector(`[data-category="${gradientCategory}"]`)
        if (activeTab) {
            activeTab.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            })
        }
    }, [gradientCategory])

    const pexelsKey = (import.meta as any).env.VITE_PEXELS_API_KEY
    const unsplashKey = (import.meta as any).env.VITE_UNSPLASH_API_KEY
    const UNSPLASH_APP_NAME = 'screenrecording'
    const unsplashFallback = (url: string, author: string, username?: string) => ({
        url,
        thumbUrl: url,
        author,
        authorUrl: username
            ? `https://unsplash.com/@${username}?utm_source=${UNSPLASH_APP_NAME}&utm_medium=referral`
            : `https://unsplash.com/s/photos/${encodeURIComponent(author)}?utm_source=${UNSPLASH_APP_NAME}&utm_medium=referral`,
        source: 'Unsplash',
        sourceUrl: `https://unsplash.com/?utm_source=${UNSPLASH_APP_NAME}&utm_medium=referral`
    })

    const xSupplyImage = (filename: string) => ({
        url: `/X-Supply/by backgrounds.supply/${filename}.webp`,
        thumbUrl: `/X-Supply/by backgrounds.supply/${filename}_thumb.webp`,
        author: "backgrounds.supply",
        authorUrl: "https://backgrounds.supply",
        source: "X.com",
        sourceUrl: "https://x.com"
    })

    // Pexels & Unsplash Fetching with Caching & Fallbacks
    useEffect(() => {
        const fetchStockPhotos = async (category: string, isSilent = false) => {
            if (category === 'X-Supply') return // Custom local category
            if (pexelsCache[category]) return // Already cached

            if (!isSilent) setIsLoadingPexels(true)

            let photos: any[] = []

            // 1. Try Unsplash API first if key present
            if (unsplashKey) {
                try {
                    const response = await fetch(`https://api.unsplash.com/search/photos?query=${encodeURIComponent(category)}&per_page=32`, {
                        headers: { Authorization: `Client-ID ${unsplashKey}` }
                    })
                    const data = await response.json()
                    if (data.results && data.results.length > 0) {
                        photos = data.results.map((p: any) => ({
                            url: p.urls.regular,
                            thumbUrl: p.urls.small || p.urls.thumb,
                            downloadLocation: p.links?.download_location,
                            author: p.user?.name || 'Unsplash Photographer',
                            authorUrl: p.user?.links?.html
                                ? `${p.user.links.html}?utm_source=${UNSPLASH_APP_NAME}&utm_medium=referral`
                                : `https://unsplash.com/?utm_source=${UNSPLASH_APP_NAME}&utm_medium=referral`,
                            source: 'Unsplash',
                            sourceUrl: `https://unsplash.com/?utm_source=${UNSPLASH_APP_NAME}&utm_medium=referral`
                        }))
                    }
                } catch (error) {
                    console.warn(`Unsplash fetch failed for ${category}`, error)
                }
            }

            // 2. Try Pexels API Backup if Unsplash returned nothing or key absent
            if (photos.length === 0 && pexelsKey) {
                try {
                    const response = await fetch(`https://api.pexels.com/v1/search?query=${encodeURIComponent(category)}&per_page=32`, {
                        headers: { Authorization: pexelsKey }
                    })
                    const data = await response.json()
                    if (data.photos && data.photos.length > 0) {
                        photos = data.photos.map((p: any) => ({
                            url: p.src.large2x || p.src.large || p.src.original,
                            thumbUrl: p.src.medium || p.src.small || p.src.tiny,
                            author: p.photographer,
                            authorUrl: p.photographer_url || 'https://www.pexels.com',
                            source: 'Pexels',
                            sourceUrl: p.url || 'https://www.pexels.com'
                        }))
                    }
                } catch (error) {
                    console.warn(`Pexels fetch failed for ${category}`, error)
                }
            }
            if (photos.length > 0) {
                setPexelsCache(prev => ({ ...prev, [category]: photos }))
            }

            if (!isSilent) setIsLoadingPexels(false)
        }

        // Fetch current active category immediately
        fetchStockPhotos(selectedCategory)

        // Pre-fetch other categories progressively
        if (!isPreFetching) {
            setIsPreFetching(true)
            const remaining = IMAGE_CATEGORIES.filter(c => c !== selectedCategory)

            const preFetchSequence = async () => {
                for (const cat of remaining) {
                    await new Promise(r => setTimeout(r, 800))
                    await fetchStockPhotos(cat, true)
                }
            }
            preFetchSequence()
        }
    }, [selectedCategory])

    const pexelsImages = pexelsCache[selectedCategory] || []

    const CATEGORY_IMAGES: Record<string, any[]> = {
        'X-Supply': [
            xSupplyImage('aether-glow-kailashsr_Waterfall_from_really_tall_mountains_and_theres_a_riv_536a5626-4fe3-4f7a-8011-036b024f9707'),
            xSupplyImage('aether-glow-kailashsr_looking_up_from_underwater_theres_sun_rays_shining_th_01453f23-1eee-4534-83b5-df0be7d3d03b'),
            xSupplyImage('aether-glow-kailashsr_sacred_grove_--ar_169_--profile_zmz1rf1_--v_7_0d734a17-b0aa-4c8a-9835-e8839efe00a0'),
            xSupplyImage('aether-glow-kailashsr_space_--ar_169_--profile_zmz1rf1_--v_7_bb006661-1362-4975-86f5-92d44c6a6e76'),
            xSupplyImage('ascii-130_rectangle'),
            xSupplyImage('ascii-135_rectangle'),
            xSupplyImage('chromatica-Image 10'),
            xSupplyImage('chromatica-Image 4'),
            xSupplyImage('elysian-Image 23'),
            xSupplyImage('elysian-Image 45'),
            xSupplyImage('elysian-Image 5'),
            xSupplyImage('elysian-Image 7'),
            xSupplyImage('gradience-Image 12'),
            xSupplyImage('horizon-Image 19'),
            xSupplyImage('iridescence-Image 11'),
            xSupplyImage('iridescence-Image 15'),
            xSupplyImage('reverie-Image 26'),
            xSupplyImage('reverie-Image 36'),
            xSupplyImage('reverie-Image 37'),
            xSupplyImage('reverie-Image 46'),
            xSupplyImage('serenox-Image 0'),
            xSupplyImage('serenox-Image 27'),
            xSupplyImage('serenox-Image 36'),
            xSupplyImage('serenox-Image 50'),
            xSupplyImage('serenox-Image 8'),
            xSupplyImage('viridian-Image 1'),
            xSupplyImage('viridian-Image 15'),
            xSupplyImage('viridian-Image 16'),
            xSupplyImage('viridian-Image 2')
        ],
        'Abstracts': [
            unsplashFallback("https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=600&q=80", "Milad Fakurian"),
            unsplashFallback("https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?auto=format&fit=crop&w=600&q=80", "Google"),
            unsplashFallback("https://images.unsplash.com/photo-1550684848-fac1c5b4e853?auto=format&fit=crop&w=600&q=80", "Pawel Czerwinski"),
            unsplashFallback("https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?auto=format&fit=crop&w=600&q=80", "Zuzana Ruttkay"),
            unsplashFallback("https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&w=600&q=80", "Cash Macanaya"),
            unsplashFallback("https://images.unsplash.com/photo-1574169208507-84376144848b?auto=format&fit=crop&w=600&q=80", "Aakash Dhage"),
            unsplashFallback("https://images.unsplash.com/photo-1541701494587-cb58502866ab?auto=format&fit=crop&w=600&q=80", "Marek Piwnicki"),
            unsplashFallback("https://images.unsplash.com/photo-1507908705549-fe8c04ec5eb3?auto=format&fit=crop&w=600&q=80", "Steve Johnson"),
            unsplashFallback("https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&w=600&q=80", "Lucas Benjamin"),
            unsplashFallback("https://images.unsplash.com/photo-1550684847-75bdda21cc95?auto=format&fit=crop&w=600&q=80", "Pawel Czerwinski"),
            unsplashFallback("https://images.unsplash.com/photo-1604076913837-52ab5629fba9?auto=format&fit=crop&w=600&q=80", "Steve Johnson"),
            unsplashFallback("https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?auto=format&fit=crop&w=600&q=80", "Pawel Czerwinski")
        ],
        'Landscapes': [
            unsplashFallback("https://images.unsplash.com/photo-1472214103451-9374bd1c798e?auto=format&fit=crop&w=600&q=80", "Luca Bravo"),
            unsplashFallback("https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=600&q=80", "Kal Vis"),
            unsplashFallback("https://images.unsplash.com/photo-1433086566280-57820a652d4b?auto=format&fit=crop&w=600&q=80", "Sora Sagano"),
            unsplashFallback("https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=600&q=80", "Bobby Birks"),
            unsplashFallback("https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=600&q=80", "Bailey Zindel"),
            unsplashFallback("https://images.unsplash.com/photo-1511884642898-4c92249e20b6?auto=format&fit=crop&w=600&q=80", "Jonatan Pie"),
            unsplashFallback("https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=600&q=80", "Pietro De Grandi"),
            unsplashFallback("https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=600&q=80", "Vicky Sim"),
            unsplashFallback("https://images.unsplash.com/photo-1426604966848-d7adac402bff?auto=format&fit=crop&w=600&q=80", "Markus Spiske"),
            unsplashFallback("https://images.unsplash.com/photo-1470240731273-7821a6eeb6bd?auto=format&fit=crop&w=600&q=80", "Dawid Zawiła")
        ],
        'Space': [
            unsplashFallback("https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=600&q=80", "NASA"),
            unsplashFallback("https://images.unsplash.com/photo-1506703719100-a0f3a48c0f86?auto=format&fit=crop&w=600&q=80", "Guillermo Ferla"),
            unsplashFallback("https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?auto=format&fit=crop&w=600&q=80", "SpaceX"),
            unsplashFallback("https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=600&q=80", "Bryan Goff"),
            unsplashFallback("https://images.unsplash.com/photo-1462331940025-496dfbfc7564?auto=format&fit=crop&w=600&q=80", "NASA"),
            unsplashFallback("https://images.unsplash.com/photo-1502134249126-9f3755a50d78?auto=format&fit=crop&w=600&q=80", "Graham Holtshausen"),
            unsplashFallback("https://images.unsplash.com/photo-1447433589675-4aaa569f3e05?auto=format&fit=crop&w=600&q=80", "NASA")
        ],
        'Greens': [
            unsplashFallback("https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?auto=format&fit=crop&w=600&q=80", "Scott Webb"),
            unsplashFallback("https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=600&q=80", "Sebastian Unrau"),
            unsplashFallback("https://images.unsplash.com/photo-1502082553048-f009c37129b9?auto=format&fit=crop&w=600&q=80", "Veeterzy"),
            unsplashFallback("https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=600&q=80", "Vicky Sim"),
            unsplashFallback("https://images.unsplash.com/photo-1511497584788-876761c119ef?auto=format&fit=crop&w=600&q=80", "Dave Hoefler"),
            unsplashFallback("https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?auto=format&fit=crop&w=600&q=80", "Jan Huber")
        ],
        'Ocean': [
            unsplashFallback("https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80", "Sean Oulashin"),
            unsplashFallback("https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&w=600&q=80", "Kalen Emsley"),
            unsplashFallback("https://images.unsplash.com/photo-1505118380757-91f5f5632de0?auto=format&fit=crop&w=600&q=80", "Matt Hardy"),
            unsplashFallback("https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=600&q=80", "Bryan Goff"),
            unsplashFallback("https://images.unsplash.com/photo-1468581264429-2548ef9eb732?auto=format&fit=crop&w=600&q=80", "Shifaaz shamoon")
        ],
        'Hills': [
            unsplashFallback("https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=600&q=80", "Bailey Zindel"),
            unsplashFallback("https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=600&q=80", "Kal Vis"),
            unsplashFallback("https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=600&q=80", "Pietro De Grandi"),
            unsplashFallback("https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=600&q=80", "Benjamin Voros")
        ],
        'Fire': [
            unsplashFallback("https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=600&q=80", "Benjamin Voros"),
            unsplashFallback("https://images.unsplash.com/photo-1509114397022-ed747cca3f65?auto=format&fit=crop&w=600&q=80", "Caleb Woods"),
            unsplashFallback("https://images.unsplash.com/photo-1517824806704-9040b037703b?auto=format&fit=crop&w=600&q=80", "Jonatan Pie"),
            unsplashFallback("https://images.unsplash.com/photo-1542224566-6e85f2e6772f?auto=format&fit=crop&w=600&q=80", "NASA")
        ],
        'Forests': [
            unsplashFallback("https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=600&q=80", "Sebastian Unrau"),
            unsplashFallback("https://images.unsplash.com/photo-1511497584788-876761c119ef?auto=format&fit=crop&w=600&q=80", "Dave Hoefler"),
            unsplashFallback("https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?auto=format&fit=crop&w=600&q=80", "Jan Huber"),
            unsplashFallback("https://images.unsplash.com/photo-1473448912268-2022ce9509d8?auto=format&fit=crop&w=600&q=80", "Veeterzy")
        ],
        'Arctic': [
            unsplashFallback("https://images.unsplash.com/photo-1483921020237-2ff51e8e4b22?auto=format&fit=crop&w=600&q=80", "Galina N"),
            unsplashFallback("https://images.unsplash.com/photo-1517411032315-54ef2cb783bb?auto=format&fit=crop&w=600&q=80", "Jonatan Pie"),
            unsplashFallback("https://images.unsplash.com/photo-1518098268026-4e89f1a2cd8b?auto=format&fit=crop&w=600&q=80", "Bryan Goff"),
            unsplashFallback("https://images.unsplash.com/photo-1491555103944-7c647fd857e6?auto=format&fit=crop&w=600&q=80", "Annie Spratt")
        ],
        'Retro': [
            unsplashFallback("https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&w=600&q=80", "Lorenzo Herrera"),
            unsplashFallback("https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=600&q=80", "Markus Spiske"),
            unsplashFallback("https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&w=600&q=80", "Carl Raw"),
            unsplashFallback("https://images.unsplash.com/photo-1534447677768-be436bb09401?auto=format&fit=crop&w=600&q=80", "Tobias Keller")
        ],
        'Minimal': [
            unsplashFallback("https://images.unsplash.com/photo-1494438639946-1ebd1d20bf85?auto=format&fit=crop&w=600&q=80", "Aaditya Airdrop"),
            unsplashFallback("https://images.unsplash.com/photo-1513542789411-b6a5d4f31634?auto=format&fit=crop&w=600&q=80", "Scott Webb"),
            unsplashFallback("https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=600&q=80", "Tim Gouw"),
            unsplashFallback("https://images.unsplash.com/photo-1508739773434-c26b3d09e071?auto=format&fit=crop&w=600&q=80", "Cristian Palmer")
        ]
    }

    const getColorsFromGradient = (val: string) => {
        const matches = val.match(/#[0-9a-fA-F]{6}/g)
        return matches || ['#667eea', '#764ba2']
    }

    const [color1, color2] = getColorsFromGradient(backgroundValue)

    const handleCustomGradientChange = (c1: string, c2: string) => {
        setBackground('gradient', `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`)
    }

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            const url = URL.createObjectURL(file)
            setBackground('image', url, { author: 'Local File', authorUrl: '', source: 'Upload', sourceUrl: '' })
        }
    }

    // Combine hardcoded and pexels
    const currentImages = [
        ...(CATEGORY_IMAGES[selectedCategory] || []),
        ...pexelsImages
    ]

    return (
        <div className="space-y-6 animate-in fade-in duration-200">
            {/* Segmented Sub-Tab Switcher: Gradients vs Stock Photos */}
            <div className="flex bg-surface-800/80 p-1 rounded-xl border border-white/5 gap-1 shadow-inner">
                <button
                    onClick={() => setBgSubTab('gradients')}
                    className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${bgSubTab === 'gradients' ? 'bg-brand-500 text-white shadow-md shadow-brand-500/20 scale-[1.02]' : 'text-txt-dim hover:text-white hover:bg-white/5'}`}
                >
                    <Palette size={14} /> Gradients
                </button>
                <button
                    onClick={() => setBgSubTab('photos')}
                    className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${bgSubTab === 'photos' ? 'bg-brand-500 text-white shadow-md shadow-brand-500/20 scale-[1.02]' : 'text-txt-dim hover:text-white hover:bg-white/5'}`}
                >
                    <ImageIcon size={14} /> Stock Photos
                </button>
            </div>

            {/* TAB 1: GRADIENTS */}
            {bgSubTab === 'gradients' && (
                <div className="space-y-6 animate-in fade-in duration-200">
                    {/* Custom Gradient & Mesh Generator */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] font-bold text-txt-dim uppercase tracking-[0.2em] block">Custom Gradient & Mesh</label>
                            <button
                                onClick={() => {
                                    const getRandomColor = () => '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')
                                    const c1 = getRandomColor()
                                    const c2 = getRandomColor()
                                    const c3 = getRandomColor()
                                    const c4 = getRandomColor()
                                    const mesh = `radial-gradient(at 10% 10%, ${c1} 0px, transparent 55%), radial-gradient(at 90% 10%, ${c2} 0px, transparent 55%), radial-gradient(at 90% 90%, ${c3} 0px, transparent 55%), radial-gradient(at 10% 90%, ${c4} 0px, transparent 55%), #050515`
                                    setBackground('gradient', mesh)
                                }}
                                className="text-[10px] font-bold text-brand-400 hover:text-brand-300 flex items-center gap-1.5 px-3 py-1 bg-brand-500/10 rounded-lg border border-brand-500/20 hover:bg-brand-500/20 transition-all active:scale-95"
                            >
                                <Sparkles size={12} /> 🎲 Random Mesh
                            </button>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex-1 flex items-center gap-2 p-2 bg-surface-800/40 rounded-xl border border-white/5">
                                <div
                                    className="w-8 h-8 rounded-lg shadow-inner shrink-0"
                                    style={{ backgroundColor: color1 }}
                                >
                                    <input
                                        type="color"
                                        value={color1}
                                        onChange={(e) => handleCustomGradientChange(e.target.value, color2)}
                                        className="w-full h-full opacity-0 cursor-pointer"
                                    />
                                </div>
                                <input
                                    type="text"
                                    value={color1.toUpperCase()}
                                    onChange={(e) => {
                                        if (/^#[0-9A-F]{6}$/i.test(e.target.value)) {
                                            handleCustomGradientChange(e.target.value, color2)
                                        }
                                    }}
                                    className="bg-transparent text-[10px] font-mono text-white w-16 outline-none focus:text-brand-400 transition-colors"
                                />
                            </div>
                            <div className="flex-1 flex items-center gap-2 p-2 bg-surface-800/40 rounded-xl border border-white/5">
                                <div
                                    className="w-8 h-8 rounded-lg shadow-inner shrink-0"
                                    style={{ backgroundColor: color2 }}
                                >
                                    <input
                                        type="color"
                                        value={color2}
                                        onChange={(e) => handleCustomGradientChange(color1, e.target.value)}
                                        className="w-full h-full opacity-0 cursor-pointer"
                                    />
                                </div>
                                <input
                                    type="text"
                                    value={color2.toUpperCase()}
                                    onChange={(e) => {
                                        if (/^#[0-9A-F]{6}$/i.test(e.target.value)) {
                                            handleCustomGradientChange(color1, e.target.value)
                                        }
                                    }}
                                    className="bg-transparent text-[10px] font-mono text-white w-16 outline-none focus:text-brand-400 transition-colors"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Film Grain Noise Intensity Slider */}
                    <div className="space-y-2 pt-2 border-t border-white/5">
                        <div className="flex items-center justify-between text-[10px]">
                            <label className="font-bold text-txt-dim uppercase tracking-[0.2em]">🌾 Film Grain Noise</label>
                            <span className="font-mono text-brand-400 font-bold">{Math.round(backgroundNoise * 100)}%</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="0.6"
                            step="0.02"
                            value={backgroundNoise}
                            onChange={(e) => setBackgroundNoise(parseFloat(e.target.value))}
                            className="w-full h-1.5 bg-surface-800 rounded-lg appearance-none cursor-pointer accent-brand-500"
                        />
                    </div>

                    {/* Gradient Category Chips */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] font-bold text-txt-dim uppercase tracking-[0.2em] block">Gradients ({filteredGradients.length})</label>
                        </div>

                        <div className="relative group/tabs">
                            <div
                                ref={gradientTabsRef}
                                className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-none relative scroll-smooth pl-1 pr-8"
                            >
                                {[
                                    { id: 'all', label: 'All' },
                                    { id: 'linear', label: '🌈 Linear' },
                                    { id: 'animated', label: '⚡ Animated Mesh' },
                                    { id: 'holographic', label: '💎 Holographic' },
                                    { id: 'texture', label: '🌾 Film Grain' }
                                ].map(cat => (
                                    <button
                                        key={cat.id}
                                        data-category={cat.id}
                                        onClick={() => setGradientCategory(cat.id as any)}
                                        className={`px-3 py-1.5 rounded-full text-[10px] border transition-all duration-200 font-bold whitespace-nowrap ${gradientCategory === cat.id ? 'bg-brand-500 border-brand-500 text-white scale-105 shadow-md shadow-brand-500/20' : 'bg-surface-800 border-white/5 text-txt-dim hover:border-white/20 hover:text-white'}`}
                                    >
                                        {cat.label}
                                    </button>
                                ))}
                            </div>

                            {/* Right Fade */}
                            <div className="absolute right-0 top-0 bottom-2 w-12 bg-gradient-to-l from-surface-900 via-surface-900/80 to-transparent z-10 pointer-events-none" />

                            {/* Navigation Arrows */}
                            <button
                                onClick={() => gradientTabsRef.current?.scrollBy({ left: -100, behavior: 'smooth' })}
                                className="absolute left-0 top-[15px] -translate-y-1/2 p-1 text-white/40 hover:text-white transition-colors z-20 opacity-0 group-hover/tabs:opacity-100"
                            >
                                <ChevronDown size={14} className="rotate-90" />
                            </button>
                            <button
                                onClick={() => gradientTabsRef.current?.scrollBy({ left: 100, behavior: 'smooth' })}
                                className="absolute right-0 top-[15px] -translate-y-1/2 p-1 text-white/40 hover:text-white transition-colors z-20"
                            >
                                <ChevronDown size={14} className="-rotate-90" />
                            </button>
                        </div>

                        <div className="grid grid-cols-8 gap-1.5">
                            {filteredGradients.map((bg) => (
                                <button
                                    key={bg.name}
                                    onClick={() => setBackground('gradient', bg.value)}
                                    title={`${bg.name} (${bg.category})`}
                                    className={`relative aspect-square rounded-md border transition-all hover:scale-110 active:scale-95 overflow-hidden ${bg.animatedClass || ''} ${backgroundValue === bg.value && backgroundType === 'gradient' ? 'border-brand-500 scale-105 shadow-lg shadow-brand-500/20 ring-2 ring-brand-500/40' : 'border-transparent hover:border-white/20'}`}
                                    style={{ background: bg.value }}
                                >
                                    {bg.category === 'animated' && (
                                        <div className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full bg-brand-400 animate-ping opacity-75" />
                                    )}
                                    {bg.category === 'texture' && (
                                        <div className="noise-overlay opacity-30" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 2: STOCK PHOTOS */}
            {bgSubTab === 'photos' && (
                <div className="space-y-4 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between">
                        <label className="text-[10px] font-bold text-txt-dim uppercase tracking-[0.2em] block">Stock Photos ({currentImages.length})</label>
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="text-[10px] font-medium text-brand-400 hover:text-brand-300 flex items-center gap-1.5 px-3 py-1 bg-brand-500/10 rounded-lg border border-brand-500/20 hover:bg-brand-500/20 transition-colors"
                        >
                            <Upload size={12} /> Upload
                        </button>
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
                    </div>

                    {/* Category Chips with Scroll Controls */}
                    <div className="group/tabs relative">
                        {/* Left Fade */}
                        <div className="absolute left-0 top-0 bottom-2 w-8 bg-gradient-to-r from-surface-900 to-transparent z-10 pointer-events-none opacity-0 group-hover/tabs:opacity-100 transition-opacity" />

                        <div
                            ref={tabsRef}
                            className="flex gap-2 overflow-x-auto pb-3 scrollbar-none relative scroll-smooth pl-1 pr-8"
                        >
                            {IMAGE_CATEGORIES.map(cat => (
                                <button
                                    key={cat}
                                    data-category={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`px-4 py-1.5 rounded-full text-[10px] border transition-all duration-200 font-bold whitespace-nowrap ${selectedCategory === cat ? 'bg-brand-500 border-brand-500 text-white scale-105 shadow-md shadow-brand-500/20' : 'bg-surface-800 border-white/5 text-txt-dim hover:border-white/20 hover:text-white'}`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>

                        {/* Right Fade */}
                        <div className="absolute right-0 top-0 bottom-2 w-12 bg-gradient-to-l from-surface-900 via-surface-900/80 to-transparent z-10 pointer-events-none" />

                        {/* Navigation Arrows */}
                        <button
                            onClick={() => tabsRef.current?.scrollBy({ left: -120, behavior: 'smooth' })}
                            className="absolute left-0 top-[18px] -translate-y-1/2 p-1 text-white/40 hover:text-white transition-colors z-20 opacity-0 group-hover/tabs:opacity-100"
                        >
                            <ChevronDown size={14} className="rotate-90" />
                        </button>
                        <button
                            onClick={() => tabsRef.current?.scrollBy({ left: 120, behavior: 'smooth' })}
                            className="absolute right-0 top-[18px] -translate-y-1/2 p-1 text-white/40 hover:text-white transition-colors z-20"
                        >
                            <ChevronDown size={14} className="-rotate-90" />
                        </button>
                    </div>

                    {/* Image Grid - 8 Columns matching Gradients Grid */}
                    <div className="relative min-h-[120px]">
                        {isLoadingPexels && (
                            <div className="absolute inset-0 flex items-center justify-center bg-surface-900/60 z-10 rounded-xl backdrop-blur-xs">
                                <RefreshCcw size={16} className="animate-spin text-brand-400" />
                            </div>
                        )}
                        <div className="grid grid-cols-8 gap-1.5">
                            {currentImages.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={async () => {
                                        setBackground('image', img.url, { author: img.author, authorUrl: img.authorUrl, source: img.source, sourceUrl: img.sourceUrl })

                                        // Trigger Unsplash download endpoint per API guidelines
                                        if (img.downloadLocation && unsplashKey) {
                                            try {
                                                await fetch(img.downloadLocation, {
                                                    headers: { Authorization: `Client-ID ${unsplashKey}` }
                                                })
                                            } catch (err) {
                                                console.warn('Failed to trigger Unsplash download endpoint:', err)
                                            }
                                        }
                                    }}
                                    className={`aspect-square rounded-md border transition-all hover:scale-110 active:scale-95 overflow-hidden ${backgroundValue === img.url && backgroundType === 'image' ? 'border-brand-500 scale-105 shadow-lg shadow-brand-500/20 ring-2 ring-brand-500/40' : 'border-transparent hover:border-white/20'}`}
                                >
                                    <img
                                        src={img.thumbUrl || img.thumbnail || img.url}
                                        className="w-full h-full object-cover"
                                        alt={img.author ? `Photo by ${img.author}` : 'stock'}
                                        loading="lazy"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        crossOrigin="anonymous"
                                        onError={(e) => {
                                            const target = e.currentTarget
                                            if (img.url && target.src !== img.url) {
                                                target.src = img.url
                                            } else {
                                                target.onerror = null
                                                target.src = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=600&q=80"
                                            }
                                        }}
                                    />
                                </button>
                            ))}
                        </div>

                        {!isLoadingPexels && currentImages.length === 0 && (
                            <div className="text-center py-8 text-[10px] text-txt-dim border border-dashed border-white/5 rounded-xl">
                                No stock photos found for {selectedCategory}
                            </div>
                        )}
                    </div>

                    {/* Subtle Compact Attribution Line */}
                    {backgroundType === 'image' && backgroundAttribution && (
                        <div className="mt-2.5 px-2.5 py-1.5 rounded-lg bg-surface-800/40 border border-white/5 flex items-center justify-between text-[10px] text-txt-dim">
                            <div className="flex items-center gap-1.5 truncate">
                                {backgroundAttribution.source.toLowerCase() === 'unsplash' ? (
                                    <svg viewBox="0 0 32 32" className="w-2.5 h-2.5 fill-current text-white/70 shrink-0">
                                        <path d="M10 9V0h12v9H10zm12 5h10v18H0V14h10v9h12v-9z" />
                                    </svg>
                                ) : backgroundAttribution.source.toLowerCase() === 'pexels' ? (
                                    <svg viewBox="0 0 500 500" className="w-2.5 h-2.5 fill-current text-[#05a081] shrink-0">
                                        <path d="M75 0h350c41.4 0 75 33.6 75 75v350c0 41.4-33.6 75-75 75H75c-41.4 0-75-33.6-75-75V75C0 33.6 33.6 0 75 0zm117 117v266h74V289h38c64.5 0 117-52.5 117-117s-52.5-117-117-117h-112zm74 62h38c30.4 0 55 24.6 55 55s-24.6 55-55 55h-38V179z" />
                                    </svg>
                                ) : (
                                    <div className="w-1 h-1 rounded-full bg-brand-400 shrink-0" />
                                )}
                                <span className="truncate">
                                    Photo by{' '}
                                    <a
                                        href={backgroundAttribution.authorUrl || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-txt-secondary font-medium hover:text-white transition-colors underline decoration-white/20 underline-offset-2"
                                    >
                                        {backgroundAttribution.author}
                                    </a>{' '}
                                    on{' '}
                                    <a
                                        href={backgroundAttribution.sourceUrl || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-txt-secondary font-medium hover:text-white transition-colors underline decoration-white/20 underline-offset-2"
                                    >
                                        {backgroundAttribution.source}
                                    </a>
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Effects (Only for Image) */}
            {backgroundType === 'image' && (
                <div className="space-y-6 pt-6 border-t border-white/5 animate-in fade-in slide-in-from-top-4">
                    <label className="text-[10px] font-bold text-txt-dim uppercase tracking-[0.2em] block">Image Effects</label>
                    <div className="space-y-4">
                        <RangeControl
                            label="Blur"
                            value={backgroundBlur}
                            min={0}
                            max={40}
                            onChange={(v: number) => setBackgroundBlur(v)}
                            unit="px"
                        />
                        <RangeControl
                            label="Opacity / Dim"
                            value={Math.round((1 - backgroundDim) * 100)}
                            min={0}
                            max={100}
                            onChange={(v: number) => setBackgroundDim(1 - v / 100)}
                            unit="%"
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
    return (
        <button
            onClick={() => onChange(!value)}
            className={`w-9 h-5 rounded-full relative transition-colors flex-shrink-0 ${value ? 'bg-brand-500' : 'bg-surface-600'}`}
        >
            <div className={`absolute top-[3px] w-3.5 h-3.5 rounded-full bg-white shadow-sm transition-all ${value ? 'left-[19px]' : 'left-[3px]'}`} />
        </button>
    )
}



function LayoutControls() {
    const {
        padding, setPadding,
        borderRadius, setBorderRadius,
        shadow, setShadow,
        perspective, setPerspective,
        perspectiveSettings, updatePerspectiveSettings,
        borderWidth, setBorderWidth,
        borderColor, setBorderColor,
        borderOpacity, setBorderOpacity,
        animationIn, setAnimationIn,
        animationOut, setAnimationOut
    } = useEditorStore()

    const [showBorderCustom, setShowBorderCustom] = useState(true)
    const [show3D, setShow3D] = useState(perspective !== 'none')
    const [showAnimation, setShowAnimation] = useState(animationIn !== 'none')
    const is3DEnabled = perspective !== 'none'

    const PERSPECTIVE_LABELS: Partial<Record<PerspectiveType, string>> = {
        'subtle-tilt':     'Subtle',
        'hero-float':      'Float',
        'tilt-left':       'Left',
        'tilt-right':      'Right',
        'isometric-left':  'Iso L',
        'isometric-right': 'Iso R',
        'dramatic-left':   'Drama L',
        'dramatic-right':  'Drama R',
    }

    const BORDER_PRESETS = [
        { label: 'White',   color: '#ffffff', opacity: 1.0,  glass: false },
        { label: 'Glass',   color: '#ffffff', opacity: 0.25, glass: true  },
        { label: 'Frosted', color: '#ffffff', opacity: 0.55, glass: false },
        { label: 'Dark',    color: '#000000', opacity: 0.7,  glass: false },
        { label: 'Subtle',  color: '#ffffff', opacity: 0.12, glass: false },
    ] as const

    const [animSubTab, setAnimSubTab] = useState<'in' | 'out'>('in')

    const ENTRANCE_PRESETS: { value: AnimationType; label: string; icon: string }[] = [
        { value: 'none',        label: 'None',       icon: '—' },
        { value: 'pop',         label: 'Pop',        icon: '💥' },
        { value: 'fade',        label: 'Fade',       icon: '✨' },
        { value: 'slide-up',    label: 'Slide Up',   icon: '↑' },
        { value: 'slide-down',  label: 'Slide Down', icon: '↓' },
        { value: 'slide-left',  label: 'Slide Left', icon: '←' },
        { value: 'slide-right', label: 'Slide Right',icon: '→' },
        { value: 'zoom-in',     label: 'Zoom In',    icon: '🔍' },
        { value: 'blur-in',     label: 'Blur In',    icon: '💧' },
        { value: 'bounce-in',   label: 'Bounce In',  icon: '⚡' },
        { value: 'rotate-in',   label: 'Rotate In',  icon: '🔄' },
        { value: 'back-in',     label: 'Back In',    icon: '🎯' },
    ]

    const EXIT_PRESETS: { value: AnimationType; label: string; icon: string }[] = [
        { value: 'none',             label: 'None',        icon: '—' },
        { value: 'fade-out',         label: 'Fade Out',    icon: '✨' },
        { value: 'slide-out-up',     label: 'Slide Up',    icon: '↑' },
        { value: 'slide-out-down',   label: 'Slide Down',  icon: '↓' },
        { value: 'slide-out-left',   label: 'Slide Left',  icon: '←' },
        { value: 'slide-out-right',  label: 'Slide Right', icon: '→' },
        { value: 'zoom-out',         label: 'Zoom Out',    icon: '🔍' },
        { value: 'blur-out',         label: 'Blur Out',    icon: '💧' },
        { value: 'bounce-out',       label: 'Bounce Out',  icon: '⚡' },
        { value: 'rotate-out',       label: 'Rotate Out',  icon: '🔄' },
    ]

    return (
        <div className="space-y-0 pb-4">

            {/* ── DIMENSIONS ── */}
            <div className="space-y-3 pb-5">
                <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Dimensions</p>
                <RangeControl label="Padding"   value={padding}      min={0}  max={80}  onChange={setPadding}      unit="px" />
                <RangeControl label="Roundness" value={borderRadius} min={0}  max={60}  onChange={setBorderRadius} unit="px" />
            </div>

            {/* ── APPEARANCE (Border + Shadow grouped) ── */}
            <div className="space-y-4 py-5 border-t border-white/[0.06]">
                <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Appearance</p>

                {/* Border */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-txt-secondary">Border</span>
                        <Toggle value={borderWidth > 0} onChange={(v) => setBorderWidth(v ? 2 : 0)} />
                    </div>
                    {borderWidth > 0 && (
                        <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-150">
                            <div className="grid grid-cols-6 gap-1">
                                {BORDER_PRESETS.map((preset) => {
                                    const isActive = borderColor === preset.color && Math.abs(borderOpacity - preset.opacity) < 0.05
                                    return (
                                        <button
                                            key={preset.label}
                                            onClick={() => { setBorderColor(preset.color); setBorderOpacity(preset.opacity) }}
                                            title={preset.label}
                                            className={`flex flex-col items-center gap-1 p-1 rounded-xl border transition-all ${isActive ? 'border-brand-500/60 bg-brand-500/10' : 'border-white/[0.06] bg-surface-800 hover:bg-white/5'}`}
                                        >
                                            <div className="w-full aspect-[4/3] rounded-lg relative overflow-hidden flex items-center justify-center"
                                                style={{ background: 'linear-gradient(135deg, #1e2a3a 0%, #0f1623 100%)' }}>
                                                <div className="w-[55%] h-[45%] rounded bg-white/10" />
                                                {preset.glass ? (
                                                    <div className="absolute inset-0 rounded-lg pointer-events-none" style={{
                                                        border: '2px solid rgba(255,255,255,0.55)',
                                                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
                                                        background: 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, transparent 50%)',
                                                    }} />
                                                ) : (
                                                    <div className="absolute inset-0 rounded-lg pointer-events-none"
                                                        style={{ border: `2px solid ${preset.color}`, opacity: preset.opacity }} />
                                                )}
                                            </div>
                                            <span className="text-[8px] text-txt-dim leading-none">{preset.label}</span>
                                        </button>
                                    )
                                })}
                                <button
                                    onClick={() => setShowBorderCustom(v => !v)}
                                    title="Custom"
                                    className={`flex flex-col items-center gap-1 p-1 rounded-xl border transition-all ${showBorderCustom ? 'border-brand-500/60 bg-brand-500/10' : 'border-white/[0.06] bg-surface-800 hover:bg-white/5'}`}
                                >
                                    <div className="w-full aspect-[4/3] rounded-lg bg-surface-900 flex items-center justify-center">
                                        <SlidersHorizontal size={10} className="text-txt-dim" />
                                    </div>
                                    <span className="text-[8px] text-txt-dim leading-none">Custom</span>
                                </button>
                            </div>
                            {showBorderCustom && (
                                <div className="space-y-2 pt-1 animate-in fade-in duration-150">
                                    <RangeControl label="Width"   value={borderWidth}                     min={1}  max={20}  onChange={setBorderWidth}                  unit="px" />
                                    <RangeControl label="Opacity" value={Math.round(borderOpacity * 100)} min={1}  max={100} onChange={(v) => setBorderOpacity(v / 100)} unit="%" />
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-txt-secondary">Color</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] text-txt-dim font-mono">{borderColor.toUpperCase()}</span>
                                            <input type="color" value={borderColor} onChange={(e) => setBorderColor(e.target.value)}
                                                className="w-7 h-7 rounded-lg bg-surface-800 border border-white/10 cursor-pointer p-0.5" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Shadow — single row */}
                <div className="space-y-2">
                    <span className="text-xs text-txt-secondary">Shadow</span>
                    <div className="flex gap-1.5">
                        {PRESET_SHADOWS.map((s) => {
                            const isActive = shadow === s.value
                            return (
                                <button
                                    key={s.name}
                                    onClick={() => setShadow(s.value)}
                                    title={s.name}
                                    className={`flex-1 flex flex-col items-center gap-1 p-1.5 rounded-xl border transition-all ${isActive ? 'border-brand-500/60 bg-brand-500/10' : 'border-white/[0.06] bg-surface-800 hover:bg-white/5'}`}
                                >
                                    <div className="w-full aspect-[1/1] rounded-lg bg-[#0a0a14] flex items-center justify-center">
                                        {s.value === 'none' ? (
                                            <div className="w-[60%] h-[60%] rounded bg-surface-600" />
                                        ) : (
                                            <div className="w-[60%] h-[60%] rounded bg-white/70"
                                                style={{ boxShadow: s.value }} />
                                        )}
                                    </div>
                                    <span className="text-[8px] text-txt-dim leading-none">{s.name}</span>
                                </button>
                            )
                        })}
                    </div>
                </div>
            </div>

            {/* ── 3D PERSPECTIVE (collapsible) ── */}
            <div className="py-5 border-t border-white/[0.06]">
                <button
                    onClick={() => setShow3D(v => !v)}
                    className="w-full flex items-center justify-between group"
                >
                    <div className="flex items-center gap-2">
                        <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest">3D Perspective</p>
                        {is3DEnabled && <span className="text-[9px] bg-brand-500/20 text-brand-400 px-1.5 py-0.5 rounded-full">On</span>}
                    </div>
                    <div className="flex items-center gap-2">
                        <Toggle value={is3DEnabled} onChange={(v) => { setPerspective(v ? 'subtle-tilt' : 'none'); setShow3D(v) }} />
                        <ChevronDown size={12} className={`text-txt-dim transition-transform ${show3D ? 'rotate-180' : ''}`} />
                    </div>
                </button>

                {show3D && is3DEnabled && (
                    <div className="space-y-4 mt-3 animate-in fade-in slide-in-from-top-2 duration-150">
                        <div className="grid grid-cols-4 gap-1">
                            {(Object.keys(PERSPECTIVE_LABELS) as PerspectiveType[]).map((p) => (
                                <button key={p} onClick={() => setPerspective(p)}
                                    className={`px-1.5 py-2 text-[10px] rounded-lg border text-center transition-all ${perspective === p ? 'bg-brand-500/20 text-brand-300 border-brand-500/30' : 'border-white/[0.06] bg-surface-800 hover:bg-white/5 text-txt-dim'}`}>
                                    {PERSPECTIVE_LABELS[p]}
                                </button>
                            ))}
                        </div>
                        <div className="space-y-2 pt-2 border-t border-white/[0.06]">
                            <RangeControl label="Pitch" value={perspectiveSettings.rotateX}     min={-80}  max={80}   onChange={(v) => updatePerspectiveSettings({ rotateX: v })}     unit="°" />
                            <RangeControl label="Tilt"  value={perspectiveSettings.rotateY}     min={-80}  max={80}   onChange={(v) => updatePerspectiveSettings({ rotateY: v })}     unit="°" />
                            <RangeControl label="Roll"  value={perspectiveSettings.rotateZ}     min={-180} max={180}  onChange={(v) => updatePerspectiveSettings({ rotateZ: v })}     unit="°" />
                            <RangeControl label="Scale" value={perspectiveSettings.scale}       min={0.5}  max={1.5}  onChange={(v) => updatePerspectiveSettings({ scale: v })}       unit="x" step={0.01} />
                            <RangeControl label="Depth" value={perspectiveSettings.perspective} min={500}  max={2000} onChange={(v) => updatePerspectiveSettings({ perspective: v })} unit="px" step={100} />
                        </div>
                    </div>
                )}
            </div>

            {/* ── ANIMATION (collapsible) ── */}
            <div className="py-5 border-t border-white/[0.06]">
                <button
                    onClick={() => setShowAnimation(v => !v)}
                    className="w-full flex items-center justify-between group"
                >
                    <div className="flex items-center gap-2">
                        <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Animation</p>
                        {animationIn !== 'none' && (
                            <span className="text-[9px] bg-brand-500/20 text-brand-400 px-2 py-0.5 rounded-full capitalize">
                                In: {animationIn}
                            </span>
                        )}
                        {animationOut !== 'none' && (
                            <span className="text-[9px] bg-pink-500/20 text-pink-400 px-2 py-0.5 rounded-full capitalize">
                                Out: {animationOut}
                            </span>
                        )}
                    </div>
                    <ChevronDown size={12} className={`text-txt-dim transition-transform ${showAnimation ? 'rotate-180' : ''}`} />
                </button>

                {showAnimation && (
                    <div className="space-y-3 mt-3 animate-in fade-in slide-in-from-top-1 duration-150">
                        {/* Sub-Tab Switcher: Entrance (In) vs Exit (Out) */}
                        <div className="flex bg-surface-850 p-1 rounded-xl border border-white/5 gap-1 shadow-inner">
                            <button
                                onClick={() => setAnimSubTab('in')}
                                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                                    animSubTab === 'in'
                                        ? 'bg-brand-500 text-white shadow-md shadow-brand-500/20'
                                        : 'text-white/40 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                Entrance (In)
                            </button>
                            <button
                                onClick={() => setAnimSubTab('out')}
                                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                                    animSubTab === 'out'
                                        ? 'bg-brand-500 text-white shadow-md shadow-brand-500/20'
                                        : 'text-white/40 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                Exit (Out)
                            </button>
                        </div>

                        {/* Presets Grid */}
                        <div className="grid grid-cols-3 gap-1.5">
                            {(animSubTab === 'in' ? ENTRANCE_PRESETS : EXIT_PRESETS).map((anim) => {
                                const currentVal = animSubTab === 'in' ? animationIn : animationOut
                                const isSelected = currentVal === anim.value
                                return (
                                    <button
                                        key={anim.value}
                                        onClick={() => {
                                            if (animSubTab === 'in') {
                                                setAnimationIn(anim.value)
                                                useEditorStore.getState().setCurrentTime(0)
                                            } else {
                                                setAnimationOut(anim.value)
                                                const dur = useEditorStore.getState().duration
                                                if (dur > 0) useEditorStore.getState().setCurrentTime(Math.max(0, dur - 0.5))
                                            }
                                        }}
                                        className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border transition-all ${
                                            isSelected
                                                ? 'bg-brand-500/15 border-brand-500/60 text-white shadow-lg shadow-brand-500/10 scale-[1.02]'
                                                : 'bg-surface-800 border-white/[0.06] text-white/40 hover:bg-surface-700/60 hover:text-white'
                                        }`}
                                    >
                                        <div className="w-7 h-7 rounded-lg bg-surface-900 flex items-center justify-center text-xs">
                                            {anim.icon}
                                        </div>
                                        <span className="text-[10px] font-medium leading-none truncate w-full text-center">{anim.label}</span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>

        </div>
    )
}

// --- NEW COMPONENT: Visual Anchor Control ---
function AnchorPointControl({
    x, y, onChange, mode = 'focus', scale = 2,
}: {
    x: number; y: number; onChange: (x: number, y: number) => void
    mode?: string; scale?: number
}) {
    const boxRef = useRef<HTMLDivElement>(null)
    const { blob } = useRecordingStore()
    const [bgUrl, setBgUrl] = useState<string>('')
    const isPanMode = mode === 'pan'

    useEffect(() => {
        if (blob) setBgUrl(URL.createObjectURL(blob))
    }, [blob])

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!boxRef.current) return
        e.preventDefault()

        if (isPanMode) {
            // Pan mode: drag the viewport box
            const rect = boxRef.current.getBoundingClientRect()
            const boxW = rect.width / scale
            const boxH = rect.height / scale
            const startMouseX = e.clientX
            const startMouseY = e.clientY
            const startBoxLeft = Math.max(0, Math.min(rect.width - boxW, ((x + 50) / 100) * rect.width - boxW / 2))
            const startBoxTop  = Math.max(0, Math.min(rect.height - boxH, ((y + 50) / 100) * rect.height - boxH / 2))

            const onMove = (mv: MouseEvent) => {
                const dx = mv.clientX - startMouseX
                const dy = mv.clientY - startMouseY
                const newLeft = Math.max(0, Math.min(rect.width - boxW, startBoxLeft + dx))
                const newTop  = Math.max(0, Math.min(rect.height - boxH, startBoxTop  + dy))
                const pctX = ((newLeft + boxW / 2) / rect.width)  * 100 - 50
                const pctY = ((newTop  + boxH / 2) / rect.height) * 100 - 50
                onChange(Math.round(pctX), Math.round(pctY))
            }
            const onUp = () => {
                window.removeEventListener('mousemove', onMove)
                window.removeEventListener('mouseup', onUp)
            }
            window.addEventListener('mousemove', onMove)
            window.addEventListener('mouseup', onUp)
        } else {
            // Focus mode: click to set center point
            const update = (cX: number, cY: number) => {
                const rect = boxRef.current!.getBoundingClientRect()
                const pctX = (Math.max(0, Math.min(rect.width,  cX - rect.left))  / rect.width)  * 100 - 50
                const pctY = (Math.max(0, Math.min(rect.height, cY - rect.top))   / rect.height) * 100 - 50
                onChange(Math.round(pctX), Math.round(pctY))
            }
            update(e.clientX, e.clientY)
            const onMove = (mv: MouseEvent) => update(mv.clientX, mv.clientY)
            const onUp = () => {
                window.removeEventListener('mousemove', onMove)
                window.removeEventListener('mouseup', onUp)
            }
            window.addEventListener('mousemove', onMove)
            window.addEventListener('mouseup', onUp)
        }
    }

    // Pan mode: viewport box dimensions as % of minimap
    const boxWPct = isPanMode ? (100 / scale) : 0
    const boxHPct = isPanMode ? (100 / scale) : 0
    const boxLeft = isPanMode ? Math.max(0, Math.min(100 - boxWPct, (x + 50) - boxWPct / 2)) : 0
    const boxTop  = isPanMode ? Math.max(0, Math.min(100 - boxHPct, (y + 50) - boxHPct / 2)) : 0

    return (
        <div className="space-y-2">
            <div className="flex justify-between">
                <label className="text-xs text-txt-secondary">
                    {isPanMode ? 'Viewport Region' : 'Center Point'}
                </label>
                <button
                    onClick={() => onChange(0, 0)}
                    className="text-[10px] text-brand-400 hover:underline flex items-center gap-1"
                >
                    <RefreshCcw size={10} /> Reset
                </button>
            </div>

            <div
                ref={boxRef}
                className={`aspect-video w-full rounded-lg bg-surface-800 border border-white/10 relative overflow-hidden group ${isPanMode ? 'cursor-grab' : 'cursor-crosshair'}`}
                onMouseDown={handleMouseDown}
            >
                {/* Video preview */}
                <video
                    src={bgUrl}
                    className="w-full h-full object-cover opacity-50 grayscale group-hover:grayscale-0 transition-all pointer-events-none"
                    muted
                />
                {/* Grid lines */}
                <div className="absolute inset-x-0 top-1/2 h-px bg-white/10 pointer-events-none" />
                <div className="absolute inset-y-0 left-1/2 w-px bg-white/10 pointer-events-none" />

                {isPanMode ? (
                    // Pan mode: SVG dark surround + rounded viewport box
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <defs>
                            <mask id="anchor-pan-mask">
                                <rect width="100" height="100" fill="white" />
                                <rect x={boxLeft} y={boxTop} width={boxWPct} height={boxHPct} rx="2" ry="2" fill="black" />
                            </mask>
                        </defs>
                        <rect width="100" height="100" fill="rgba(0,0,0,0.5)" mask="url(#anchor-pan-mask)" />
                        <rect x={boxLeft} y={boxTop} width={boxWPct} height={boxHPct} rx="2" ry="2"
                            fill="none" stroke="#3b82f6" strokeWidth="0.8" />
                    </svg>
                ) : (
                    // Focus mode: dot
                    <div
                        className="absolute w-4 h-4 bg-brand-500 rounded-full border-2 border-white shadow-lg shadow-brand-500/50 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                        style={{ left: `${x + 50}%`, top: `${y + 50}%` }}
                    />
                )}

                {/* Move icon for pan mode */}
                {isPanMode && (
                    <div className="absolute pointer-events-none"
                        style={{ left: `${boxLeft + boxWPct / 2}%`, top: `${boxTop + boxHPct / 2}%`, transform: 'translate(-50%,-50%)' }}>
                        <Move size={10} className="text-blue-400 opacity-80" />
                    </div>
                )}
            </div>
            <div className="text-[10px] text-txt-dim text-center">
                {isPanMode ? 'Drag the box to set the pan viewport' : 'Click and drag to adjust the zoom center point'}
            </div>
        </div>
    )
}

function ZoomControls() {
    const { zoomRegions, updateZoomRegion, removeZoomRegion, currentTime, generateAutoZoom, selectedZoomRegionId } = useEditorStore()

    // Find active region, prioritizing selected zoom region
    const activeRegion = zoomRegions.find(r => r.id === selectedZoomRegionId) || zoomRegions.find(r => currentTime >= r.startTime && currentTime <= r.endTime)

    return (
        <div className="space-y-6">
            {!activeRegion ? (
                <div className="space-y-4">
                    <div className="p-4 bg-surface-800/50 rounded-lg border border-white/5 text-center space-y-3">
                        <p className="text-sm text-txt-secondary">No zoom active at this time.</p>
                        <p className="text-xs text-txt-dim">Click on the <strong>Zoom Track</strong> below to add a zoom region.</p>
                    </div>

                    {zoomRegions.length === 0 && (
                        <div className="pt-4 border-t border-white/5">
                            <button
                                onClick={() => {
                                    const recState = useRecordingStore.getState()
                                    if (recState.isDesktop) {
                                        // Desktop: use uiohook data stored in recordingStore
                                        generateAutoZoom(recState.mouseData, recState.screenWidth, recState.screenHeight)
                                    } else {
                                        // Extension/browser: use mouseTracker singleton
                                        import('@/lib/mouse-tracker').then(({ mouseTracker }) => {
                                            const events = mouseTracker.getEvents()
                                            generateAutoZoom(events, recState.screenWidth, recState.screenHeight)
                                        })
                                    }
                                }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-brand-500/10 text-brand-300 rounded-lg hover:bg-brand-500/20 transition-colors text-xs font-medium border border-brand-500/20"
                            >
                                <ZoomIn size={14} />
                                Auto-Generate Zoom (Beta)
                            </button>
                            <p className="text-[10px] text-txt-dim mt-2 text-center">
                                Uses cursor pauses to automatically create zoom regions.
                            </p>
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-6 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between pb-4 border-b border-white/5">
                        <span className="text-xs font-mono text-brand-300 bg-brand-500/10 px-2 py-0.5 rounded">
                            {activeRegion.startTime.toFixed(1)}s - {activeRegion.endTime.toFixed(1)}s
                        </span>
                        <button
                            onClick={() => removeZoomRegion(activeRegion.id)}
                            className="flex items-center gap-1.5 px-2 py-1 text-xs text-danger-400 hover:bg-danger-500/10 rounded transition-colors"
                        >
                            <Trash2 size={12} /> Remove
                        </button>
                    </div>

                    <RangeControl
                        label="Zoom Level"
                        value={activeRegion.scale}
                        min={1}
                        max={3}
                        step={0.1}
                        onChange={(val: number) => updateZoomRegion(activeRegion.id, { scale: val })}
                        unit="x"
                    />

                    {/* New Anchor Point Control */}
                    <div className="flex items-center justify-between">
                        <label className="text-xs text-txt-secondary">Zoom Mode</label>
                        <div className="flex bg-surface-900 rounded-lg p-0.5 border border-white/10">
                            <button
                                onClick={() => updateZoomRegion(activeRegion.id, { mode: 'focus' })}
                                className={`p-1.5 rounded-md transition-all ${!activeRegion.mode || activeRegion.mode === 'focus' ? 'bg-surface-700 text-white shadow-sm' : 'text-txt-dim hover:text-white'}`}
                                title="Focus Mode (Smart Camera)"
                            >
                                <Target size={14} />
                            </button>
                            <button
                                onClick={() => updateZoomRegion(activeRegion.id, { mode: 'pan' })}
                                className={`p-1.5 rounded-md transition-all ${activeRegion.mode === 'pan' ? 'bg-surface-700 text-white shadow-sm' : 'text-txt-dim hover:text-white'}`}
                                title="Pan Mode (Move Image)"
                            >
                                <Hand size={14} />
                            </button>
                        </div>
                    </div>

                    <AnchorPointControl
                        x={activeRegion.x}
                        y={activeRegion.y}
                        onChange={(x, y) => updateZoomRegion(activeRegion.id, { x, y })}
                        mode={activeRegion.mode}
                        scale={activeRegion.scale}
                    />

                    <div className="pt-4 border-t border-white/5">
                        <label className="text-xs text-txt-secondary mb-2 block">Easing Curve</label>
                        <select
                            value={activeRegion.ease || 'easeInOut'}
                            onChange={(e) => updateZoomRegion(activeRegion.id, { ease: e.target.value as EaseType })}
                            className="w-full bg-surface-800 border border-white/10 rounded-md text-xs text-white p-2 outline-none focus:border-brand-500 cursor-pointer"
                        >
                            <option value="easeInOut">Smooth (Ease In Out)</option>
                            <option value="easeOut">Ease Out</option>
                            <option value="linear">Linear</option>
                        </select>
                    </div>
                </div>
            )}
        </div>
    )
}

function ControlSection({ title, children, defaultOpen = true }: { title: string, children: React.ReactNode, defaultOpen?: boolean }) {
    const [isOpen, setIsOpen] = useState(defaultOpen)
    return (
        <div className="border-b border-white/[0.04] last:border-0 transition-all duration-300">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between py-4 px-2 group cursor-pointer"
            >
                <span className={`text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-300 ${isOpen ? 'text-white' : 'text-white/40 group-hover:text-white/60'}`}>
                    {title}
                </span>
                <div className={`transition-transform duration-300 ease-out ${isOpen ? 'rotate-180 text-brand-400' : 'text-white/20'}`}>
                    <ChevronDown size={14} />
                </div>
            </button>
            <div
                className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100 pb-6' : 'grid-rows-[0fr] opacity-0 pb-0'}`}
            >
                <div className="overflow-hidden">
                    <div className="px-2 space-y-4">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    )
}

// --- Helper Components for Premium UI ---

function CompactCard({ children, className = "" }: { children: React.ReactNode, className?: string }) {
    return (
        <div className={`bg-white/[0.03] border border-white/[0.05] rounded-2xl p-4 space-y-4 hover:border-white/10 transition-colors shadow-sm ${className}`}>
            {children}
        </div>
    )
}

function SegmentedControl({ options, value, onChange }: { options: { id: string, label?: string, icon?: React.ReactNode }[], value: string, onChange: (id: string) => void }) {
    return (
        <div className="flex bg-surface-950/50 rounded-xl p-1 border border-white/5 w-full">
            {options.map((opt) => (
                <button
                    key={opt.id}
                    onClick={() => onChange(opt.id)}
                    className={`flex-1 py-1.5 flex items-center justify-center rounded-lg transition-all text-xs font-medium ${value === opt.id ? 'bg-surface-700 text-white shadow-sm' : 'text-white/40 hover:text-white/70'}`}
                >
                    {opt.icon || opt.label}
                </button>
            ))}
        </div>
    )
}

function ProSelect({ label, value, onChange, options }: any) {
    const [isOpen, setIsOpen] = useState(false)
    const currentOption = options.find((opt: any) => opt.value === value)

    return (
        <div className="space-y-2 relative">
            {label && <label className="text-[10px] text-white/40 font-bold uppercase tracking-widest px-1">{label}</label>}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl text-xs text-white p-2.5 pl-3 pr-8 outline-none focus:border-brand-500/30 focus:bg-white/[0.06] transition-all cursor-pointer hover:bg-white/[0.05] hover:border-white/10 text-left relative"
            >
                <span className="truncate block">{currentOption?.label || 'Select...'}</span>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/20">
                    <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>
            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-surface-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 max-h-64 overflow-y-auto">
                        {options.map((opt: any) => (
                            <button
                                key={opt.value}
                                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-xs transition-all ${opt.value === value ? 'bg-brand-500/20 text-brand-400' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

// Custom fonts stored in state
const useCustomFonts = () => {
    const [customFonts, setCustomFonts] = useState<{ name: string, family: string }[]>(() => {
        try {
            const stored = localStorage.getItem('customFonts')
            return stored ? JSON.parse(stored) : []
        } catch { return [] }
    })

    const addFont = (name: string, family: string) => {
        const updated = [...customFonts, { name, family }]
        setCustomFonts(updated)
        localStorage.setItem('customFonts', JSON.stringify(updated))
    }

    return { customFonts, addFont }
}

function FontSelect({ value, onChange }: { value: string, onChange: (val: string) => void }) {
    const [isOpen, setIsOpen] = useState(false)
    const { customFonts, addFont } = useCustomFonts()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const defaultFonts = [
        { label: 'Outfit', value: 'Outfit', category: 'Display' },
        { label: 'Inter', value: 'Inter', category: 'Sans' },
        { label: 'Roboto', value: 'Roboto', category: 'Sans' },
        { label: 'Manrope', value: 'Manrope', category: 'Sans' },
        { label: 'Quicksand', value: 'Quicksand', category: 'Rounded' },
        { label: 'Space Grotesk', value: 'Space Grotesk', category: 'Grotesk' },
        { label: 'Lexend Deca', value: 'Lexend Deca', category: 'Sans' },
        { label: 'Short Stack', value: 'Short Stack', category: 'Handwritten' },
        { label: 'Righteous', value: 'Righteous', category: 'Display' },
        { label: 'Sixtyfour', value: 'Sixtyfour', category: 'Display' },
        { label: 'Marko One', value: 'Marko One', category: 'Serif' },
        { label: 'Playfair Display', value: 'Playfair Display', category: 'Serif' },
    ]

    const allFonts = [
        ...defaultFonts,
        ...customFonts.map(f => ({ label: f.name, value: f.family, category: 'Custom' }))
    ]

    const currentFont = allFonts.find(f => f.value === value) || defaultFonts[0]

    const handleFontUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        const fontName = file.name.replace(/\.(ttf|otf|woff|woff2)$/i, '')
        const fontFamily = `custom-${fontName}-${Date.now()}`

        try {
            const arrayBuffer = await file.arrayBuffer()
            const fontFace = new FontFace(fontFamily, arrayBuffer)
            await fontFace.load()
            document.fonts.add(fontFace)
            addFont(fontName, fontFamily)
            onChange(fontFamily)
            setIsOpen(false)
        } catch (err) {
            console.error('Failed to load font:', err)
        }

        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    return (
        <div className="space-y-2 relative">
            <label className="text-[10px] text-white/40 font-bold uppercase tracking-widest px-1">Font Family</label>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl text-xs text-white p-2.5 pl-3 pr-8 outline-none focus:border-brand-500/30 focus:bg-white/[0.06] transition-all cursor-pointer hover:bg-white/[0.05] hover:border-white/10 text-left relative"
            >
                <span className="truncate block" style={{ fontFamily: currentFont.value }}>{currentFont.label}</span>
                <span className="absolute right-8 top-1/2 -translate-y-1/2 text-[8px] text-white/20 uppercase">{currentFont.category}</span>
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-white/20">
                    <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>
            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-surface-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="max-h-56 overflow-y-auto">
                            {allFonts.map((font) => (
                                <button
                                    key={font.value}
                                    onClick={() => { onChange(font.value); setIsOpen(false); }}
                                    className={`w-full text-left px-3 py-2.5 text-sm transition-all flex items-center justify-between group ${font.value === value ? 'bg-brand-500/20 text-brand-400' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                                >
                                    <span style={{ fontFamily: font.value }}>{font.label}</span>
                                    <span className="text-[8px] text-white/20 uppercase group-hover:text-white/40">{font.category}</span>
                                </button>
                            ))}
                        </div>
                        <div className="border-t border-white/10 p-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".ttf,.otf,.woff,.woff2"
                                onChange={handleFontUpload}
                                className="hidden"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full py-2 px-3 text-xs text-white/50 hover:text-brand-400 hover:bg-brand-500/10 rounded-lg transition-all flex items-center justify-center gap-2 border border-transparent hover:border-brand-500/30"
                            >
                                <Upload size={12} />
                                Upload Custom Font
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

const SUBTITLE_LAYER_DEFAULTS = (currentTime: number) => ({
    id: crypto.randomUUID(),
    content: 'Subtitle text',
    startTime: currentTime,
    endTime: currentTime + 3,
    fontSize: 20,
    color: '#ffffff',
    fontFamily: 'Inter',
    fontWeight: '600',
    textAlign: 'center' as const,
    x: 50,
    y: 95,
    opacity: 1,
    letterSpacing: 0,
    lineHeight: 1.3,
    textDecoration: 'none' as const,
    textTransform: 'none' as const,
    strokeEnabled: false,
    textGradient: '',
    animationIn: 'none' as const,
})

function TextControls() {
    const {
        textLayers,
        selectedTextLayerId,
        updateTextLayer,
        removeTextLayer,
        addTextLayer,
        currentTime,
    } = useEditorStore()
    
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [subTab, setSubTab] = useState<'subtitles' | 'style'>('subtitles')
    const [subtitlePanel, setSubtitlePanel] = useState<'none' | 'import' | 'builder'>('none')
    const [importCount, setImportCount] = useState<number | null>(null)
    const [builderText, setBuilderText] = useState('')
    const [builderStart, setBuilderStart] = useState<number | null>(null)
    const [importError, setImportError] = useState<string | null>(null)
    const [appliedAllToast, setAppliedAllToast] = useState(false)
    const [gapAmount, setGapAmount] = useState(0.05)

    const handleApplyGaps = () => {
        const sorted = [...textLayers].sort((a, b) => a.startTime - b.startTime)
        for (let i = 0; i < sorted.length - 1; i++) {
            const current = sorted[i]
            const next = sorted[i + 1]
            if (next.startTime - current.endTime < gapAmount) {
                const newEndTime = Math.max(current.startTime + 0.1, next.startTime - gapAmount)
                updateTextLayer(current.id, { endTime: Number(newEndTime.toFixed(2)) })
            }
        }
    }

    const handleMoveUp = (l: any) => {
        const sorted = [...textLayers].sort((a, b) => a.startTime - b.startTime)
        const idx = sorted.findIndex(item => item.id === l.id)
        if (idx > 0) {
            const prev = sorted[idx - 1]
            const currentContent = l.content
            updateTextLayer(l.id, { content: prev.content })
            updateTextLayer(prev.id, { content: currentContent })
            useEditorStore.getState().setSelectedTextLayer(prev.id)
        }
    }

    const handleMoveDown = (l: any) => {
        const sorted = [...textLayers].sort((a, b) => a.startTime - b.startTime)
        const idx = sorted.findIndex(item => item.id === l.id)
        if (idx < sorted.length - 1) {
            const next = sorted[idx + 1]
            const currentContent = l.content
            updateTextLayer(l.id, { content: next.content })
            updateTextLayer(next.id, { content: currentContent })
            useEditorStore.getState().setSelectedTextLayer(next.id)
        }
    }

    useEffect(() => {
        if (selectedTextLayerId) {
            setSubtitlePanel('none')
        }
    }, [selectedTextLayerId])

    const formatTime = (t: number) => {
        const m = Math.floor(t / 60).toString().padStart(2, '0')
        const s = (t % 60).toFixed(1).padStart(4, '0')
        return `${m}:${s}`
    }

    const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setImportError(null)
        const reader = new FileReader()
        reader.onload = async (ev) => {
            try {
                const content = ev.target?.result as string
                const { parseSubtitles } = await import('@/lib/subtitle-parser')
                const cues = parseSubtitles(file.name, content)
                if (cues.length === 0) { setImportError('No subtitles found — check the file format.'); return }
                useEditorStore.setState({ textLayers: [] })
                cues.forEach(cue => addTextLayer({
                    ...SUBTITLE_LAYER_DEFAULTS(cue.startTime),
                    id: crypto.randomUUID(),
                    content: cue.text,
                    startTime: cue.startTime,
                    endTime: cue.endTime,
                }))
                setImportCount(cues.length)
                setTimeout(() => setImportCount(null), 4000)
            } catch { setImportError('Could not parse file.') }
        }
        reader.readAsText(file)
        e.target.value = ''
    }

    const handleBuilderSetStart = () => setBuilderStart(currentTime)
    const handleBuilderCommit = () => {
        if (builderStart === null || !builderText.trim()) return
        addTextLayer({
            ...SUBTITLE_LAYER_DEFAULTS(builderStart),
            id: crypto.randomUUID(),
            content: builderText.trim(),
            startTime: builderStart,
            endTime: currentTime,
        })
        setBuilderText('')
        setBuilderStart(null)
    }

    const activeLayer = textLayers.find(l => l.id === selectedTextLayerId) ||
        textLayers.find(l => currentTime >= l.startTime && currentTime <= l.endTime)

    const handleApplyStyleToAll = () => {
        if (!activeLayer) return
        textLayers.forEach(l => {
            if (l.id !== activeLayer.id) {
                updateTextLayer(l.id, {
                    fontSize: activeLayer.fontSize,
                    color: activeLayer.color,
                    fontFamily: activeLayer.fontFamily,
                    fontWeight: activeLayer.fontWeight,
                    textAlign: activeLayer.textAlign,
                    x: activeLayer.x,
                    y: activeLayer.y,
                    opacity: activeLayer.opacity,
                    letterSpacing: activeLayer.letterSpacing,
                    lineHeight: activeLayer.lineHeight,
                    textDecoration: activeLayer.textDecoration,
                    textTransform: activeLayer.textTransform,
                    backgroundColor: activeLayer.backgroundColor,
                    backgroundOpacity: activeLayer.backgroundOpacity,
                    backgroundPadding: activeLayer.backgroundPadding,
                    backgroundBorderRadius: activeLayer.backgroundBorderRadius,
                    textGradient: activeLayer.textGradient,
                    strokeEnabled: activeLayer.strokeEnabled,
                    strokeColor: activeLayer.strokeColor,
                    strokeWidth: activeLayer.strokeWidth,
                    textShadow: activeLayer.textShadow,
                    animationIn: activeLayer.animationIn,
                    animationOut: activeLayer.animationOut,
                })
            }
        })
        setAppliedAllToast(true)
        setTimeout(() => setAppliedAllToast(false), 2000)
    }

    return (
        <div className="animate-in fade-in duration-300 space-y-1">
            {/* Sub-tab Switcher */}
            <div className="flex bg-white/[0.02] border border-white/[0.05] p-1 rounded-2xl mb-4">
                <button
                    onClick={() => setSubTab('subtitles')}
                    className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-xl transition-all ${
                        subTab === 'subtitles'
                            ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20'
                            : 'text-white/40 hover:text-white/70'
                    }`}
                >
                    Subtitles
                </button>
                <button
                    onClick={() => setSubTab('style')}
                    className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-xl transition-all ${
                        subTab === 'style'
                            ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20'
                            : 'text-white/40 hover:text-white/70'
                    }`}
                >
                    Design Style
                </button>
            </div>

            {subTab === 'subtitles' ? (
                <div className="space-y-4 animate-in fade-in duration-200">
                    {/* Hidden file input */}
                    <input ref={fileInputRef} type="file" accept=".srt,.vtt" className="hidden" onChange={handleImportFile} />

                    {/* Quick Action Buttons */}
                    <div className="grid grid-cols-3 gap-2">
                        <button
                            onClick={() => addTextLayer(SUBTITLE_LAYER_DEFAULTS(currentTime))}
                            className="flex flex-col items-center gap-2 p-3 bg-white/[0.03] hover:bg-brand-500/10 border border-white/[0.06] hover:border-brand-500/30 rounded-2xl transition-all group"
                        >
                            <div className="w-8 h-8 bg-brand-500/10 group-hover:bg-brand-500/20 rounded-xl flex items-center justify-center transition-all">
                                <Type size={15} className="text-brand-400" />
                            </div>
                            <span className="text-[9px] font-bold text-white/40 group-hover:text-brand-300 uppercase tracking-wider">Add Text</span>
                        </button>
                        <button
                            onClick={() => setSubtitlePanel(p => p === 'builder' ? 'none' : 'builder')}
                            className={`flex flex-col items-center gap-2 p-3 border rounded-2xl transition-all group ${
                                subtitlePanel === 'builder' ? 'bg-brand-500/15 border-brand-500/40' : 'bg-white/[0.03] hover:bg-brand-500/10 border-white/[0.06] hover:border-brand-500/30'
                            }`}
                        >
                            <div className="w-8 h-8 bg-brand-500/10 group-hover:bg-brand-500/20 rounded-xl flex items-center justify-center transition-all">
                                <AlignCenter size={15} className="text-brand-400" />
                            </div>
                            <span className="text-[9px] font-bold text-white/40 group-hover:text-brand-300 uppercase tracking-wider">Builder</span>
                        </button>
                        <button
                            onClick={() => setSubtitlePanel(p => p === 'import' ? 'none' : 'import')}
                            className={`flex flex-col items-center gap-2 p-3 border rounded-2xl transition-all group ${
                                subtitlePanel === 'import' ? 'bg-brand-500/15 border-brand-500/40' : 'bg-white/[0.03] hover:bg-brand-500/10 border-white/[0.06] hover:border-brand-500/30'
                            }`}
                        >
                            <div className="w-8 h-8 bg-brand-500/10 group-hover:bg-brand-500/20 rounded-xl flex items-center justify-center transition-all">
                                <Upload size={15} className="text-brand-400" />
                            </div>
                            <span className="text-[9px] font-bold text-white/40 group-hover:text-brand-300 uppercase tracking-wider">Import</span>
                        </button>
                    </div>

                    {/* Subtitle Builder panel */}
                    {subtitlePanel === 'builder' && (
                        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-bold text-white/50 uppercase tracking-widest">✍️ Subtitle Builder</span>
                                    <div className="relative group flex items-center shrink-0">
                                        <CircleHelp size={12} className="text-white/40 hover:text-white/70 cursor-pointer transition-colors" />
                                        <div className="absolute left-0 bottom-full mb-2 w-48 bg-gray-950 border border-white/10 text-white/80 text-[10px] leading-relaxed rounded-lg p-2.5 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all shadow-xl z-50">
                                            Play the video → click <b>Set Start</b> at start of speech → click <b>Set End</b> when speech ends.
                                        </div>
                                    </div>
                                </div>
                                <span className="text-[9px] text-brand-400 font-mono bg-brand-500/10 px-2 py-0.5 rounded-full">▶ {formatTime(currentTime)}</span>
                            </div>
                            <textarea
                                value={builderText}
                                onChange={e => setBuilderText(e.target.value)}
                                placeholder="Type subtitle line or paste raw SRT/VTT content..."
                                rows={4}
                                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white placeholder-white/20 outline-none resize-none focus:border-brand-500/50 transition-colors font-mono"
                            />
                            {builderText.includes('-->') ? (
                                <button
                                    onClick={async () => {
                                        const { parseSubtitleTextString } = await import('@/lib/subtitle-parser')
                                        const cues = parseSubtitleTextString(builderText)
                                        if (cues.length > 0) {
                                            useEditorStore.setState({ textLayers: [] })
                                            cues.forEach(cue => {
                                                addTextLayer({
                                                    ...SUBTITLE_LAYER_DEFAULTS(cue.startTime),
                                                    id: crypto.randomUUID(),
                                                    content: cue.text,
                                                    startTime: cue.startTime,
                                                    endTime: cue.endTime,
                                                })
                                            })
                                            setBuilderText('')
                                            setSubtitlePanel('none')
                                        }
                                    }}
                                    className="w-full py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-wider bg-brand-500 text-white hover:bg-brand-400 transition-all flex items-center justify-center gap-1.5"
                                >
                                    <Sparkles size={12} /> Parse & Apply Timed Subtitles
                                </button>
                            ) : (
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={handleBuilderSetStart}
                                        className={`py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-all ${
                                            builderStart !== null ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-white/[0.05] text-white/40 hover:text-white/70 hover:bg-white/[0.08] border border-white/[0.06]'
                                        }`}
                                    >
                                        {builderStart !== null ? `▶ ${formatTime(builderStart)}` : '[ Set Start'}
                                    </button>
                                    <button
                                        onClick={handleBuilderCommit}
                                        disabled={builderStart === null || !builderText.trim()}
                                        className="py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider bg-brand-500 text-white hover:bg-brand-400 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        ] Set End + Add
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Import panel */}
                    {subtitlePanel === 'import' && (
                        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            <span className="text-[10px] font-bold text-white/50 uppercase tracking-widest block">📂 Import Subtitle File</span>
                            {importCount !== null && (
                                <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-xl px-3 py-2">
                                    <Check size={12} className="text-green-400" />
                                    <span className="text-[11px] text-green-400">{importCount} subtitles imported</span>
                                </div>
                            )}
                            {importError && (
                                <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                                    <X size={12} className="text-red-400" />
                                    <span className="text-[11px] text-red-400">{importError}</span>
                                </div>
                            )}
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full py-5 border-2 border-dashed border-white/[0.1] hover:border-brand-500/40 rounded-xl text-center transition-all group hover:bg-brand-500/5"
                            >
                                <Upload size={18} className="text-white/20 group-hover:text-brand-400 mx-auto mb-2 transition-colors" />
                                <p className="text-[11px] text-white/40 group-hover:text-white/60">Click to upload .srt or .vtt</p>
                                <p className="text-[9px] text-white/20 mt-1">Subtitles will be auto-timed and added as layers</p>
                            </button>
                        </div>
                    )}

                    {/* All Subtitles List */}
                    {textLayers.length > 0 && (
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between px-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-[9px] text-white/20 font-bold uppercase tracking-widest">Subtitle Tracks ({textLayers.length})</span>
                                    <button
                                        onClick={() => {
                                            if (confirm('Are you sure you want to clear all text/subtitle tracks? This cannot be undone.')) {
                                                useEditorStore.setState({ textLayers: [], selectedTextLayerId: null })
                                            }
                                        }}
                                        className="text-[8px] font-bold text-danger-400 hover:text-danger-300 bg-danger-500/10 hover:bg-danger-500/20 px-1.5 py-0.5 rounded transition-all uppercase tracking-wider shrink-0"
                                        title="Delete all text layers"
                                    >
                                        Clear All
                                    </button>
                                </div>
                                <div className="flex items-center gap-1.5 bg-white/[0.03] border border-white/[0.06] rounded-xl px-2 py-1 shrink-0">
                                    <span className="text-[8px] text-white/40 font-bold uppercase">Gap</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        max="1.0"
                                        value={gapAmount}
                                        onChange={(e) => setGapAmount(Number(e.target.value))}
                                        className="w-8 bg-transparent text-[9px] text-white font-mono outline-none text-center"
                                    />
                                    <span className="text-[8px] text-white/30 mr-0.5">s</span>
                                    <button
                                        onClick={handleApplyGaps}
                                        className="text-[8px] font-bold text-brand-400 hover:text-brand-300 bg-brand-500/10 hover:bg-brand-500/20 px-1.5 py-0.5 rounded transition-all"
                                        title="Auto-insert timing gaps between all subtitles"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                                {[...textLayers].sort((a, b) => a.startTime - b.startTime).map(l => {
                                    const isSelected = l.id === selectedTextLayerId
                                    if (isSelected) {
                                        return (
                                            <div
                                                key={l.id}
                                                className="bg-brand-500/10 border border-brand-500/20 rounded-2xl p-3.5 space-y-3 animate-in fade-in duration-200"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center min-w-0 gap-1.5">
                                                        <span className="text-[9px] font-bold text-brand-400 uppercase tracking-widest flex items-center gap-1 shrink-0">
                                                            ✏️ Edit subtitle
                                                            <span className="text-[8px] text-white/30 font-mono font-normal">
                                                                ({(l.endTime - l.startTime).toFixed(1)}s)
                                                            </span>
                                                        </span>
                                                        <div className="flex items-center gap-1 bg-white/[0.04] border border-white/[0.08] rounded-lg p-0.5 shrink-0">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    handleMoveUp(l)
                                                                }}
                                                                className="p-0.5 hover:bg-white/10 rounded text-white/60 hover:text-white transition-colors"
                                                                title="Move line up (swap content with previous)"
                                                            >
                                                                <ChevronUp size={10} />
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    handleMoveDown(l)
                                                                }}
                                                                className="p-0.5 hover:bg-white/10 rounded text-white/60 hover:text-white transition-colors"
                                                                title="Move line down (swap content with next)"
                                                            >
                                                                <ChevronDown size={10} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2.5">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                removeTextLayer(l.id)
                                                            }}
                                                            className="text-danger-400 hover:text-danger-300 text-[9px] font-bold uppercase tracking-wider hover:underline"
                                                        >
                                                            Delete
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                useEditorStore.getState().setSelectedTextLayer(null)
                                                            }}
                                                            className="text-brand-400 hover:text-brand-300 text-[9px] font-bold uppercase tracking-wider hover:underline"
                                                        >
                                                            Done
                                                        </button>
                                                    </div>
                                                </div>
                                                <textarea
                                                    value={l.content}
                                                    onChange={(e) => updateTextLayer(l.id, { content: e.target.value })}
                                                    placeholder="Type subtitle text..."
                                                    rows={2}
                                                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none resize-none focus:border-brand-500/50 transition-colors"
                                                />
                                                <div className="flex items-center gap-1.5">
                                                    <div className="flex-1 flex items-center bg-white/[0.02] rounded-xl px-2 py-1 border border-white/[0.06] justify-between">
                                                        <div className="flex items-center min-w-0">
                                                            <span className="text-[8px] text-white/20 font-bold uppercase mr-1 shrink-0">In</span>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={Number(l.startTime.toFixed(2))}
                                                                onChange={(e) => updateTextLayer(l.id, { startTime: Number(e.target.value) })}
                                                                className="w-12 bg-transparent text-[10px] text-white py-0.5 outline-none font-mono min-w-0"
                                                            />
                                                        </div>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                updateTextLayer(l.id, { startTime: Number(currentTime.toFixed(2)) })
                                                            }}
                                                            className="text-[8px] font-bold text-brand-400 hover:text-brand-300 bg-brand-500/10 hover:bg-brand-500/20 px-1.5 py-0.5 rounded transition-all shrink-0"
                                                            title="Set In to current time"
                                                        >
                                                            [ Set
                                                        </button>
                                                    </div>
                                                    <div className="flex-1 flex items-center bg-white/[0.02] rounded-xl px-2 py-1 border border-white/[0.06] justify-between">
                                                        <div className="flex items-center min-w-0">
                                                            <span className="text-[8px] text-white/20 font-bold uppercase mr-1 shrink-0">Out</span>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={Number(l.endTime.toFixed(2))}
                                                                onChange={(e) => updateTextLayer(l.id, { endTime: Number(e.target.value) })}
                                                                className="w-12 bg-transparent text-[10px] text-white py-0.5 outline-none font-mono min-w-0"
                                                            />
                                                        </div>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                updateTextLayer(l.id, { endTime: Number(currentTime.toFixed(2)) })
                                                            }}
                                                            className="text-[8px] font-bold text-brand-400 hover:text-brand-300 bg-brand-500/10 hover:bg-brand-500/20 px-1.5 py-0.5 rounded transition-all shrink-0"
                                                            title="Set Out to current time"
                                                        >
                                                            Set ]
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    return (
                                        <div
                                            key={l.id}
                                            onClick={() => {
                                                useEditorStore.getState().setSelectedTextLayer(l.id)
                                                useEditorStore.getState().setCurrentTime(l.startTime)
                                            }}
                                            className="flex items-center gap-2 bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04] rounded-xl px-3 py-2 group cursor-pointer transition-all"
                                        >
                                            <span className="font-mono text-[9px] text-brand-400/60 shrink-0">
                                                {formatTime(l.startTime)}
                                            </span>
                                            <span className="text-[11px] text-white/70 flex-1 truncate">{l.content}</span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    removeTextLayer(l.id)
                                                }}
                                                className="opacity-0 group-hover:opacity-100 transition-opacity text-danger-400/60 hover:text-danger-400"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-1 animate-in fade-in duration-200">
                    {/* Style tab: customization controls */}
                    {!activeLayer ? (
                        <div className="p-8 bg-white/[0.02] border border-white/[0.05] rounded-3xl text-center mt-2">
                            <div className="w-12 h-12 bg-brand-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                                <Palette size={20} className="text-brand-400" />
                            </div>
                            <p className="text-xs text-white/60 font-medium">No layer active</p>
                            <p className="text-[10px] text-white/20 mt-1 px-4">
                                Select a subtitle or text block from the list or timeline to design its style.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Apply style to all subtitles button */}
                            {textLayers.length > 1 && (
                                <div className="space-y-2">
                                    <button
                                        onClick={handleApplyStyleToAll}
                                        className="w-full py-2.5 bg-brand-500/10 hover:bg-brand-500/20 text-brand-400 border border-brand-500/20 hover:border-brand-500/30 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2"
                                    >
                                        ✨ Apply Style to All Subtitles
                                    </button>
                                    {appliedAllToast && (
                                        <div className="bg-green-500/10 border border-green-500/20 text-green-400 text-[10px] py-1.5 px-3 rounded-lg text-center font-bold uppercase tracking-wider animate-in fade-in duration-300">
                                            ✓ Applied styling to all {textLayers.length} layers!
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Section: Content */}
                            <ControlSection title="Content">
                                <CompactCard>
                                    <textarea
                                        value={activeLayer.content}
                                        onChange={(e) => updateTextLayer(activeLayer.id, { content: e.target.value })}
                                        placeholder="Type something cinematic..."
                                        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl text-[13px] leading-relaxed text-white p-3 outline-none focus:border-brand-500/50 focus:bg-white/[0.05] min-h-[80px] resize-none transition-all placeholder:text-white/10"
                                    />
                                    <div className="pt-2">
                                        <div className="flex items-center gap-2 mb-3 px-1">
                                            <div className="h-px flex-1 bg-white/5" />
                                            <span className="text-[9px] text-white/20 font-bold uppercase tracking-widest whitespace-nowrap">Timeline Visibility</span>
                                            <div className="h-px flex-1 bg-white/5" />
                                        </div>
                                        <div className="flex items-center gap-2 bg-white/[0.02] border border-white/[0.05] rounded-2xl p-1.5 focus-within:border-brand-500/20 transition-all">
                                            <div className="flex-1 relative group/input">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[9px] text-white/10 font-bold uppercase pointer-events-none group-focus-within/input:text-brand-500/40">In</span>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={activeLayer.startTime}
                                                    onChange={(e) => updateTextLayer(activeLayer.id, { startTime: Number(e.target.value) })}
                                                    className="w-full bg-transparent text-[11px] text-white/80 py-2 pl-8 pr-2 outline-none font-mono"
                                                />
                                            </div>
                                            <div className="w-px h-4 bg-white/10" />
                                            <div className="flex-1 relative group/input">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[9px] text-white/10 font-bold uppercase pointer-events-none group-focus-within/input:text-brand-500/40">Out</span>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={activeLayer.endTime}
                                                    onChange={(e) => updateTextLayer(activeLayer.id, { endTime: Number(e.target.value) })}
                                                    className="w-full bg-transparent text-[11px] text-white/80 py-2 pl-9 pr-2 outline-none font-mono"
                                                />
                                            </div>
                                            <div className="bg-brand-500/10 text-brand-400 text-[9px] font-mono px-2 py-2 rounded-xl border border-brand-500/5 min-w-[50px] text-center">
                                                {(activeLayer.endTime - activeLayer.startTime).toFixed(1)}s
                                            </div>
                                        </div>
                                    </div>
                                </CompactCard>
                            </ControlSection>


            {/* Section: Typography */}
            <ControlSection title="Typography">
                <CompactCard>
                    <FontSelect
                        value={activeLayer.fontFamily}
                        onChange={(val: string) => updateTextLayer(activeLayer.id, { fontFamily: val })}
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <ProSelect
                            label="Weight"
                            value={activeLayer.fontWeight}
                            onChange={(val: string) => updateTextLayer(activeLayer.id, { fontWeight: val })}
                            options={[
                                { label: 'Light', value: '300' },
                                { label: 'Regular', value: '400' },
                                { label: 'Medium', value: '500' },
                                { label: 'Bold', value: '700' },
                                { label: 'Black', value: '900' },
                            ]}
                        />
                        <div className="space-y-2">
                            <label className="text-[10px] text-white/40 font-bold uppercase tracking-widest px-1">Size</label>
                            <div className="relative">
                                <input
                                    type="number"
                                    value={activeLayer.fontSize}
                                    onChange={(e) => updateTextLayer(activeLayer.id, { fontSize: Number(e.target.value) })}
                                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl text-xs text-white px-3 py-2.5 outline-none focus:border-brand-500/30 transition-all font-mono"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-white/20 font-bold">PX</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <label className="text-[10px] text-white/40 font-bold uppercase tracking-widest px-1">Spacing</label>
                            <div className="flex items-center gap-2 bg-white/[0.02] border border-white/[0.05] rounded-xl px-2 py-1.5 focus-within:border-white/10 transition-colors">
                                <MoveHorizontal size={12} className="text-white/20" />
                                <input
                                    type="number"
                                    value={activeLayer.letterSpacing || 0}
                                    onChange={(e) => updateTextLayer(activeLayer.id, { letterSpacing: Number(e.target.value) })}
                                    className="w-full bg-transparent border-0 text-[11px] text-white outline-none font-mono"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] text-white/40 font-bold uppercase tracking-widest px-1">Height</label>
                            <div className="flex items-center gap-2 bg-white/[0.02] border border-white/[0.05] rounded-xl px-2 py-1.5 focus-within:border-white/10 transition-colors">
                                <MoveVertical size={12} className="text-white/20" />
                                <input
                                    type="number"
                                    step="0.1"
                                    value={activeLayer.lineHeight || 1.2}
                                    onChange={(e) => updateTextLayer(activeLayer.id, { lineHeight: Number(e.target.value) })}
                                    className="w-full bg-transparent border-0 text-[11px] text-white outline-none font-mono"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] text-white/40 font-bold uppercase tracking-widest px-1">Style & Case</label>
                        <div className="flex flex-col gap-3">
                            <div className="flex gap-2">
                                <SegmentedControl
                                    value={activeLayer.textTransform || 'none'}
                                    onChange={(val) => updateTextLayer(activeLayer.id, { textTransform: val as any })}
                                    options={[
                                        { id: 'none', icon: <CaseSensitive size={16} /> },
                                        { id: 'uppercase', icon: <CaseUpper size={16} /> },
                                        { id: 'lowercase', icon: <CaseLower size={16} /> },
                                    ]}
                                />
                                <SegmentedControl
                                    value={activeLayer.textDecoration || 'none'}
                                    onChange={(val) => updateTextLayer(activeLayer.id, { textDecoration: val as any })}
                                    options={[
                                        { id: 'none', label: 'Ab' },
                                        { id: 'underline', icon: <Underline size={14} /> },
                                        { id: 'line-through', icon: <Strikethrough size={14} /> },
                                    ]}
                                />
                            </div>
                            <SegmentedControl
                                value={activeLayer.textAlign}
                                onChange={(val) => updateTextLayer(activeLayer.id, { textAlign: val as any })}
                                options={[
                                    { id: 'left', icon: <AlignLeft size={16} /> },
                                    { id: 'center', icon: <AlignCenter size={16} /> },
                                    { id: 'right', icon: <AlignRight size={16} /> },
                                ]}
                            />
                        </div>
                    </div>
                </CompactCard>
            </ControlSection>

            {/* Section: Appearance & Background */}
            <ControlSection title="Appearance">
                <CompactCard>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] text-white/40 font-bold uppercase tracking-widest px-1">Fill Style</label>
                            <label className="flex items-center gap-2 cursor-pointer group/toggle">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={!!activeLayer.textGradient}
                                    onChange={(e) => updateTextLayer(activeLayer.id, { textGradient: e.target.checked ? 'linear-gradient(to right, #6366f1, #a855f7)' : '' })}
                                />
                                <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest group-hover/toggle:text-white/60 transition-colors">Gradient</span>
                                <div className={`w-8 h-4 rounded-full relative transition-colors ${activeLayer.textGradient ? 'bg-brand-500' : 'bg-white/10'}`}>
                                    <div className={`absolute top-1 w-2 h-2 rounded-full bg-white transition-all ${activeLayer.textGradient ? 'left-5' : 'left-1'}`} />
                                </div>
                            </label>
                        </div>

                        {!activeLayer.textGradient ? (
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl p-2 pr-3 focus-within:border-brand-500/30 transition-all flex-shrink-0">
                                    <div className="relative w-7 h-7 rounded-lg overflow-hidden border border-white/10 shadow-lg cursor-pointer hover:scale-105 transition-transform flex-shrink-0">
                                        <div
                                            className="absolute inset-0 z-0"
                                            style={{
                                                background: 'conic-gradient(from 0deg, red, yellow, lime, aqua, blue, magenta, red)',
                                                opacity: 0.8
                                            }}
                                        />
                                        <Palette className="absolute inset-0 m-auto text-white drop-shadow-md pointer-events-none" size={14} />
                                        <input
                                            type="color"
                                            value={activeLayer.color}
                                            onChange={(e) => updateTextLayer(activeLayer.id, { color: e.target.value })}
                                            className="absolute inset-[-8px] w-[200%] h-[200%] cursor-pointer opacity-0"
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[7.5px] text-white/20 font-bold uppercase tracking-tighter">Hex Code</span>
                                        <input
                                            type="text"
                                            value={activeLayer.color || '#FFFFFF'}
                                            onChange={(e) => updateTextLayer(activeLayer.id, { color: e.target.value })}
                                            className="bg-transparent border-0 text-[10px] text-white font-mono p-0 outline-none uppercase tracking-wider placeholder:text-white/10 w-[55px]"
                                            placeholder="#FFFFFF"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-1 justify-end flex-1 px-1">
                                    {['#FFFFFF', '#FF3B3B', '#4CE0B3', '#6366F1', '#EAB308', '#EC4899', '#8B5CF6'].map(c => (
                                        <button
                                            key={c}
                                            onClick={() => updateTextLayer(activeLayer.id, { color: c })}
                                            className={`w-3.5 h-3.5 rounded-full border-2 transition-all ${activeLayer.color.toLowerCase() === c.toLowerCase() ? 'border-brand-500 scale-125 shadow-lg' : 'border-white/10 hover:border-white/30 hover:scale-110'}`}
                                            style={{ backgroundColor: c }}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {/* Custom Gradient Builder */}
                                <div className="flex items-center gap-3 bg-white/[0.03] border border-white/[0.06] rounded-xl p-2.5 focus-within:border-brand-500/30 transition-all">
                                    <div className="flex items-center gap-2 flex-1">
                                        <div className="flex -space-x-2">
                                            {[
                                                activeLayer.textGradient?.match(/#[0-9a-fA-F]{6}/g)?.[0] || '#6366F1',
                                                activeLayer.textGradient?.match(/#[0-9a-fA-F]{6}/g)?.[1] || '#A855F7'
                                            ].map((c, i) => (
                                                <div key={i} className="relative w-7 h-7 rounded-full border-2 border-surface-900 overflow-hidden shadow-lg group/swatch">
                                                    <input
                                                        type="color"
                                                        value={c}
                                                        onChange={(e) => {
                                                            const colors = activeLayer.textGradient?.match(/#[0-9a-fA-F]{6}/g) || ['#6366F1', '#A855F7'];
                                                            const newColors = [...colors];
                                                            newColors[i] = e.target.value;
                                                            updateTextLayer(activeLayer.id, { textGradient: `linear-gradient(to right, ${newColors[0]}, ${newColors[1]})` });
                                                        }}
                                                        className="absolute inset-[-4px] w-[200%] h-[200%] cursor-pointer"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-[7.5px] text-white/20 font-bold uppercase tracking-tighter">Custom Blend</span>
                                            <span className="text-[9px] text-white/40 font-mono uppercase">
                                                {activeLayer.textGradient?.match(/#[0-9a-fA-F]{6}/g)?.[0] || '#6366F1'} → {activeLayer.textGradient?.match(/#[0-9a-fA-F]{6}/g)?.[1] || '#A855F7'}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        className="w-10 h-6 rounded-lg border border-white/10"
                                        style={{ backgroundImage: activeLayer.textGradient || 'linear-gradient(to right, #6366f1, #a855f7)' }}
                                    />
                                </div>

                                {/* Presets Grid */}
                                <div className="grid grid-cols-4 gap-2 pb-1">
                                    {[
                                        'linear-gradient(to right, #6366f1, #a855f7)',
                                        'linear-gradient(to right, #f43f5e, #fb923c)',
                                        'linear-gradient(to right, #10b981, #3b82f6)',
                                        'linear-gradient(to right, #FF3CAC, #784BA0)',
                                        'linear-gradient(to right, #ff9966, #ff5e62)',
                                        'linear-gradient(to right, #00f2fe, #4facfe)',
                                        'linear-gradient(to right, #8e2de2, #4a00e0)',
                                        'linear-gradient(to right, #f093fb, #f5576c)',
                                    ].map((grad, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => updateTextLayer(activeLayer.id, { textGradient: grad })}
                                            className={`h-8 rounded-lg border-2 transition-all active:scale-95 ${activeLayer.textGradient === grad ? 'border-brand-500 scale-105 shadow-brand-500/20 shadow-lg' : 'border-white/5 hover:border-white/20'}`}
                                            style={{ backgroundImage: grad }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <RangeControl
                        label="Opacity"
                        value={Math.round(activeLayer.opacity * 100)}
                        min={0}
                        max={100}
                        onChange={(val: number) => updateTextLayer(activeLayer.id, { opacity: val / 100 })}
                        unit="%"
                    />

                    <div className="pt-2 space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] text-white/40 font-bold uppercase tracking-widest px-1">Text Background</label>
                            <button
                                onClick={() => updateTextLayer(activeLayer.id, { backgroundColor: activeLayer.backgroundColor ? '' : '#000000', backgroundOpacity: 0.5, backgroundPadding: 20 })}
                                className={`text-[9px] font-bold px-2 py-0.5 rounded-full transition-all ${activeLayer.backgroundColor ? 'bg-brand-500 text-white' : 'bg-white/5 text-white/30 hover:text-white/50'}`}
                            >
                                {activeLayer.backgroundColor ? 'ACTIVE' : 'ADD'}
                            </button>
                        </div>
                        {activeLayer.backgroundColor && (
                            <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="flex items-center gap-3 bg-white/[0.02] p-2 rounded-xl border border-white/[0.05]">
                                    <div className="w-6 h-6 rounded border border-white/10 overflow-hidden relative flex-shrink-0">
                                        <input
                                            type="color"
                                            value={activeLayer.backgroundColor || '#000000'}
                                            onChange={(e) => updateTextLayer(activeLayer.id, { backgroundColor: e.target.value })}
                                            className="absolute inset-[-4px] w-[200%] h-[200%] cursor-pointer"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <RangeControl
                                            label="Opacity"
                                            value={Math.round((activeLayer.backgroundOpacity ?? 1) * 100)}
                                            min={0}
                                            max={100}
                                            onChange={(val: number) => updateTextLayer(activeLayer.id, { backgroundOpacity: val / 100 })}
                                            unit="%"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <RangeControl
                                        label="Padding"
                                        value={activeLayer.backgroundPadding || 0}
                                        min={0}
                                        max={100}
                                        onChange={(val: number) => updateTextLayer(activeLayer.id, { backgroundPadding: val })}
                                        unit="PX"
                                    />
                                    <RangeControl
                                        label="Round"
                                        value={activeLayer.backgroundBorderRadius || 0}
                                        min={0}
                                        max={50}
                                        onChange={(val: number) => updateTextLayer(activeLayer.id, { backgroundBorderRadius: val })}
                                        unit="PX"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Text Stroke Toggle */}
                    <div className="pt-2 space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] text-white/40 font-bold uppercase tracking-widest px-1">Text Stroke</label>
                            <button
                                onClick={() => updateTextLayer(activeLayer.id, { strokeEnabled: !activeLayer.strokeEnabled, strokeColor: '#000000', strokeWidth: 2 })}
                                className={`text-[9px] font-bold px-2 py-0.5 rounded-full transition-all ${activeLayer.strokeEnabled ? 'bg-brand-500 text-white' : 'bg-white/5 text-white/30 hover:text-white/50'}`}
                            >
                                {activeLayer.strokeEnabled ? 'ON' : 'OFF'}
                            </button>
                        </div>
                        {activeLayer.strokeEnabled && (
                            <div className="flex items-center gap-3 bg-white/[0.02] p-2 rounded-xl border border-white/[0.05] animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="w-6 h-6 rounded border border-white/10 overflow-hidden relative flex-shrink-0">
                                    <input
                                        type="color"
                                        value={activeLayer.strokeColor || '#000000'}
                                        onChange={(e) => updateTextLayer(activeLayer.id, { strokeColor: e.target.value })}
                                        className="absolute inset-[-4px] w-[200%] h-[200%] cursor-pointer"
                                    />
                                </div>
                                <div className="flex-1">
                                    <RangeControl
                                        label="Width"
                                        value={activeLayer.strokeWidth || 2}
                                        min={1}
                                        max={10}
                                        onChange={(val: number) => updateTextLayer(activeLayer.id, { strokeWidth: val })}
                                        unit="PX"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </CompactCard>
            </ControlSection>

            {/* Section: Motion Presets */}
            <ControlSection title="Motion Presets">
                <CompactCard>
                    <TextMotionPresets activeLayer={activeLayer} updateTextLayer={updateTextLayer} />
                </CompactCard>
            </ControlSection>

            {/* Section: Layout & Alignment */}
            <ControlSection title="Alignment & Layout">
                <CompactCard>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] text-white/40 font-bold uppercase tracking-widest px-1">Snapping</label>
                            <Target size={12} className="text-white/20" />
                        </div>
                        <div className="flex gap-4">
                            <div className="shrink-0">
                                <PositionPresets
                                    currentX={activeLayer.x}
                                    currentY={activeLayer.y}
                                    onChange={(x, y, align) => {
                                        updateTextLayer(activeLayer.id, { x, y, textAlign: align as any })
                                    }}
                                />
                            </div>
                            <div className="flex-1 space-y-4 pt-1">
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                        <span className="text-[8px] text-white/20 font-bold uppercase pl-1">Axis X</span>
                                        <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl px-2 py-1 flex items-center gap-1">
                                            <input
                                                type="number"
                                                value={Math.round(activeLayer.x)}
                                                onChange={(e) => updateTextLayer(activeLayer.id, { x: Number(e.target.value) })}
                                                className="w-full bg-transparent border-0 text-[11px] text-white font-mono outline-none"
                                            />
                                            <span className="text-[8px] text-white/20">%</span>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[8px] text-white/20 font-bold uppercase pl-1">Axis Y</span>
                                        <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl px-2 py-1 flex items-center gap-1">
                                            <input
                                                type="number"
                                                value={Math.round(activeLayer.y)}
                                                onChange={(e) => updateTextLayer(activeLayer.id, { y: Number(e.target.value) })}
                                                className="w-full bg-transparent border-0 text-[11px] text-white font-mono outline-none"
                                            />
                                            <span className="text-[8px] text-white/20">%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </CompactCard>
                <div className="pt-2">
                    <button
                        onClick={() => removeTextLayer(activeLayer.id)}
                        className="w-full py-2.5 flex items-center justify-center gap-2 rounded-xl bg-danger-500/10 text-danger-400 text-xs font-bold uppercase tracking-wider hover:bg-danger-500/20 transition-all border border-danger-500/20"
                    >
                        <Trash2 size={14} /> Delete Layer
                    </button>
                </div>
            </ControlSection>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

function PositionPresets({ currentX, currentY, onChange }: { currentX: number, currentY: number, onChange: (x: number, y: number, align: string) => void }) {
    const [mode, setMode] = useState<'canvas' | 'video'>('canvas')

    const presets = mode === 'canvas' ? [
        { name: 'Top Left', x: 5, y: 5, align: 'left' },
        { name: 'Top Center', x: 50, y: 5, align: 'center' },
        { name: 'Top Right', x: 95, y: 5, align: 'right' },
        { name: 'Center Left', x: 5, y: 50, align: 'left' },
        { name: 'Center', x: 50, y: 50, align: 'center' },
        { name: 'Center Right', x: 95, y: 50, align: 'right' },
        { name: 'Bottom Left', x: 5, y: 95, align: 'left' },
        { name: 'Bottom Center', x: 50, y: 95, align: 'center' },
        { name: 'Bottom Right', x: 95, y: 95, align: 'right' },
    ] : [
        { name: 'Top Left', x: 15, y: 15, align: 'left' },
        { name: 'Top Center', x: 50, y: 15, align: 'center' },
        { name: 'Top Right', x: 85, y: 15, align: 'right' },
        { name: 'Center Left', x: 15, y: 50, align: 'left' },
        { name: 'Center', x: 50, y: 50, align: 'center' },
        { name: 'Center Right', x: 85, y: 50, align: 'right' },
        { name: 'Bottom Left', x: 15, y: 85, align: 'left' },
        { name: 'Bottom Center', x: 50, y: 85, align: 'center' },
        { name: 'Bottom Right', x: 85, y: 85, align: 'right' },
    ]

    return (
        <div className="space-y-2">
            <div className="flex gap-4 items-center">
                <div className="grid grid-cols-3 gap-1.5 w-[90px] h-[90px] p-2.5 bg-white/[0.03] backdrop-blur-xl rounded-2xl border border-white/[0.06] shadow-xl relative group-hover:border-white/10 transition-colors">
                    {presets.map((pos, i) => {
                        const isActive = Math.abs(currentX - pos.x) < 2 && Math.abs(currentY - pos.y) < 2
                        return (
                            <button
                                key={i}
                                onClick={() => onChange(pos.x, pos.y, pos.align)}
                                className={`w-full h-full rounded-md transition-all duration-200 relative ${isActive ? 'bg-brand-500 scale-110 z-10' : 'bg-white/5 hover:bg-white/10'}`}
                                title={pos.name}
                            >
                                {isActive && <div className="absolute inset-0 bg-brand-400 animate-ping rounded-md opacity-20 pointer-events-none" />}
                            </button>
                        )
                    })}
                </div>

                <div className="flex flex-col bg-white/[0.03] rounded-xl p-1 border border-white/[0.06] shadow-2xl space-y-1">
                    <button
                        onClick={() => setMode('canvas')}
                        className={`p-2 rounded-lg transition-all duration-300 ${mode === 'canvas' ? 'bg-white/10 text-brand-400 shadow-[0_2px_10px_rgba(0,0,0,0.2)]' : 'text-white/30 hover:text-white/70'}`}
                        title="Canvas Mode"
                    >
                        <Monitor size={15} />
                    </button>
                    <button
                        onClick={() => setMode('video')}
                        className={`p-2 rounded-lg transition-all duration-300 ${mode === 'video' ? 'bg-white/10 text-brand-400 shadow-[0_2px_10px_rgba(0,0,0,0.2)]' : 'text-white/30 hover:text-white/70'}`}
                        title="Video Mode"
                    >
                        <Box size={15} />
                    </button>
                </div>
            </div>
        </div>
    )
}

function RangeControl({ label, value, min, max, onChange, unit, step = 1 }: { label: string; value: number; min: number; max: number; onChange: (v: number) => void; unit?: string; step?: number }) {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState('')

    const displayValue = typeof value === 'number' ? (step < 1 ? value.toFixed(2) : value.toFixed(0)) : value

    return (
        <div className="space-y-1.5 group/range">
            <div className="flex justify-between items-center">
                <label className="text-[10px] text-white/40 font-bold uppercase tracking-[0.12em]">{label}</label>
                {editing ? (
                    <input
                        autoFocus
                        className="w-14 text-right text-[11px] font-mono bg-surface-800 border border-brand-500/50 rounded px-1 py-0.5 outline-none text-white"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={() => {
                            const n = parseFloat(draft)
                            if (!isNaN(n)) onChange(Math.min(max, Math.max(min, n)))
                            setEditing(false)
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                            if (e.key === 'Escape') setEditing(false)
                        }}
                    />
                ) : (
                    <button
                        onClick={() => { setDraft(displayValue); setEditing(true) }}
                        className="flex items-baseline gap-0.5 hover:bg-white/5 rounded px-1 -mr-1 transition-colors"
                    >
                        <span className="text-[11px] text-white font-mono font-medium">{displayValue}</span>
                        <span className="text-[8px] text-white/20 font-bold uppercase">{unit}</span>
                    </button>
                )}
            </div>
            <div className="relative flex items-center h-4">
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="w-full accent-brand-500 h-[3px] bg-white/[0.08] rounded-full appearance-none cursor-pointer block hover:bg-white/[0.15] transition-all relative z-10"
                />
                <div
                    className="absolute left-0 top-1/2 -translate-y-1/2 h-[3px] bg-gradient-to-r from-brand-600 to-brand-400 rounded-full opacity-60 pointer-events-none"
                    style={{ width: `${Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100))}%` }}
                />
            </div>
        </div>
    )
}

function TextMotionPresets({ activeLayer, updateTextLayer }: { activeLayer: any; updateTextLayer: any }) {
    const [subTab, setSubTab] = useState<'in' | 'out'>('in')

    const ENTRANCE = [
        { id: 'none', label: 'None', icon: '—' },
        { id: 'pop', label: 'Pop', icon: '💥' },
        { id: 'fade', label: 'Fade', icon: '✨' },
        { id: 'slide-up', label: 'Rise ↑', icon: '↑' },
        { id: 'slide-down', label: 'Drop ↓', icon: '↓' },
        { id: 'slide-left', label: 'Slide ←', icon: '←' },
        { id: 'slide-right', label: 'Slide →', icon: '→' },
        { id: 'zoom-in', label: 'Zoom', icon: '🔍' },
        { id: 'blur-in', label: 'Focus', icon: '💧' },
        { id: 'bounce-in', label: 'Bounce', icon: '⚡' },
        { id: 'rotate-in', label: 'Rotate', icon: '🔄' },
        { id: 'typewriter', label: 'Typewriter', icon: '▍' },
    ]

    const EXIT = [
        { id: 'none', label: 'None', icon: '—' },
        { id: 'fade-out', label: 'Fade Out', icon: '✨' },
        { id: 'slide-out-up', label: 'Slide Up', icon: '↑' },
        { id: 'slide-out-down', label: 'Slide Down', icon: '↓' },
        { id: 'slide-out-left', label: 'Slide Left', icon: '←' },
        { id: 'slide-out-right', label: 'Slide Right', icon: '→' },
        { id: 'zoom-out', label: 'Zoom Out', icon: '🔍' },
        { id: 'blur-out', label: 'Blur Out', icon: '💧' },
        { id: 'bounce-out', label: 'Bounce Out', icon: '⚡' },
        { id: 'rotate-out', label: 'Rotate Out', icon: '🔄' },
    ]

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest">Text Motion</span>
                <Sparkles size={12} className="text-brand-400" />
            </div>

            {/* Sub-Tab Switcher */}
            <div className="flex bg-surface-850 p-1 rounded-xl border border-white/5 gap-1 shadow-inner">
                <button
                    onClick={() => setSubTab('in')}
                    className={`flex-1 py-1 rounded-lg text-[11px] font-medium transition-all ${
                        subTab === 'in' ? 'bg-brand-500 text-white shadow-md shadow-brand-500/20' : 'text-white/40 hover:text-white'
                    }`}
                >
                    Entrance (In)
                </button>
                <button
                    onClick={() => setSubTab('out')}
                    className={`flex-1 py-1 rounded-lg text-[11px] font-medium transition-all ${
                        subTab === 'out' ? 'bg-brand-500 text-white shadow-md shadow-brand-500/20' : 'text-white/40 hover:text-white'
                    }`}
                >
                    Exit (Out)
                </button>
            </div>

            {/* Presets Grid */}
            <div className="grid grid-cols-3 gap-1.5">
                {(subTab === 'in' ? ENTRANCE : EXIT).map((anim) => {
                    const currentVal = subTab === 'in' ? activeLayer.animationIn : activeLayer.animationOut
                    const isSelected = currentVal === anim.id
                    return (
                        <button
                            key={anim.id}
                            onClick={() => {
                                if (subTab === 'in') {
                                    updateTextLayer(activeLayer.id, { animationIn: anim.id })
                                    useEditorStore.getState().setCurrentTime(activeLayer.startTime)
                                } else {
                                    updateTextLayer(activeLayer.id, { animationOut: anim.id })
                                    useEditorStore.getState().setCurrentTime(Math.max(activeLayer.startTime, activeLayer.endTime - 0.4))
                                }
                            }}
                            title={anim.label}
                            className={`py-2 px-1 rounded-xl border text-[10px] font-medium transition-all flex flex-col items-center gap-1 ${
                                isSelected
                                    ? 'bg-brand-500 border-brand-400 text-white shadow-lg shadow-brand-500/20'
                                    : 'bg-white/[0.02] border-white/[0.06] text-white/40 hover:text-white/70 hover:bg-white/[0.05]'
                            }`}
                        >
                            <span className="text-xs">{anim.icon}</span>
                            <span className="text-[9px] truncate w-full text-center">{anim.label}</span>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
